/**
 * Frontend-only types and display constants for the flow module.
 *
 * Domain models (Manifest, StepNode, TriggerNode, ModuleManifest, WorkflowDefinition)
 * come directly from the auto-generated API layer (src/app/api/model/).
 * This file contains ONLY UI/rendering concerns that do not exist in the backend.
 */
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {
  faArrowRightToBracket,
  faBookOpen,
  faBracketsCurly,
  faBriefcaseArrowRight,
  faCalculatorSimple,
  faCirclesOverlap,
  faCloud,
  faFile,
  faFileLines,
  faFilter,
  faFolderTree,
  faInputNumeric,
  faListCheck,
  faPlay,
  faSparkles,
  faTextSize,
} from '@fortawesome/pro-solid-svg-icons';
import {faIfc} from '../util/icons/custom-icons';

import {ModuleManifest} from '../../api/model/moduleManifest';
import {Manifest} from '../../api/model/manifest';
import {StepNode} from '../../api/model/stepNode';
import {normalizeFileTypeToken} from '../../models/ruleset-file-match';
import {isValueField, matchesVisibleWhen} from './schema-form/widget-registry';
import {isValueFieldEmpty} from '../checks/bimCheck/components/ids/models/ids.model';

// ═══════════════════════════════════════════════════════════════════════════════
// Frontend-only types
// ═══════════════════════════════════════════════════════════════════════════════

/** A single port on a rendered node (derived from module JSON schema at render time) */
export interface NodePort {
  name: string;
  connected?: boolean;
  /**
   * JSON-Schema-Typ: 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array' | 'any' | 'flow'.
   * Eine Liste (JSON Schema erlaubt `"type": ["number","boolean"]`) beschreibt einen Port, der
   * mehrere Typen annimmt — z.B. Wertevergleich: Zahl oder Wahrheitswert, aber kein Text.
   */
  dataType?: string | string[];
  isFlowPort?: boolean; // True for execution-sequence ports (not data ports)
}

/** Constant flow ports prepended to every node */
export const FLOW_IN_PORT: NodePort = {name: '▶', dataType: 'flow', isFlowPort: true};
export const FLOW_OUT_PORT: NodePort = {name: '▶', dataType: 'flow', isFlowPort: true};

/** One outgoing case of a branching node: `key` goes into the manifest, `label` onto the port. */
export interface BranchCase {
  key: string;
  label: string;
}

/**
 * How a node branches. Derived from the module manifest (plus the node's settings), never from a
 * hardcoded module-id list.
 *
 * Two flavours:
 * - `legacy: 'thenElse'` — the binary Dann/Sonst of Bedingung/Wertevergleich/Textvergleich. Those
 *   modules declare `aktiver_pfad` (dann/sonst) in their `outputSchema`; it is exactly the output
 *   the engine reads to pick `onThen` vs `onElse` (WorkflowEngine.resolveBranchResult).
 * - n-wertig — a module declares `x-branch` on the output property carrying the decision
 *   (KI-Anweisung: `pruefergebnis`). Serialized as the manifest's `onBranch` map.
 */
export interface BranchSpec {
  /** Output-schema property the engine reads for the decision. */
  outputKey: string;
  /** Index in this array === flow-out port index. */
  cases: BranchCase[];
  legacy?: 'thenElse';
}

/**
 * Woraus die Fälle einer Verzweigung entstehen, wenn sie nicht statisch im Modul stehen, sondern
 * der Konfiguration des Nutzers folgen (Vorbild: `x-varCountSource` beim Formel-Modul).
 *
 * Zwei Ausprägungen, bewusst in einem Key:
 * - **Ein Item = ein Case** (Bedingungs-Modul): nur `path` + `labelProperty`. Jeder Eintrag des
 *   Array-Settings wird ein Ausgang.
 * - **Ein Item trägt die Optionsliste** (KI-Anweisung): `path` + `where` findet den einen
 *   Ausgabeparameter mit „als Verzweigung nutzen", `optionsProperty` liefert dessen Optionen und
 *   `sourceKeyFrom` den Output-Schlüssel, unter dem der Handler den Wert zurückgibt.
 */
export interface BranchCasesFromSetting {
  /** Array-Setting, das die Konfiguration trägt. */
  path: string;
  /** Optionaler Item-Filter — ohne Treffer entstehen 0 Fälle (= keine Verzweigung). */
  where?: { property: string; equals: any };
  /** Item-Feld mit der Optionsliste (Array oder Trennzeichen-String). */
  optionsProperty?: string;
  /** Item-Feld mit dem Label, wenn das Item selbst der Fall ist. */
  labelProperty?: string;
  /** Item-Feld, aus dem der Output-Schlüssel kommt (statt des Property-Namens). */
  sourceKeyFrom?: string;
}

/** The `x-branch` extension as declared in a module's output schema. */
interface BranchExtension {
  /** Verzweigung nur aktiv, wenn dieses Setting truthy ist. */
  enabledBySetting?: string;
  /** Verzweigung abgeschaltet, wenn dieses Setting truthy ist (Konfliktauflösung). */
  disabledBySetting?: string;
  /** Statische Fälle. */
  cases?: { key?: string; label?: string }[];
  /** Fälle aus der Nutzer-Konfiguration. Hat Vorrang vor `cases`. */
  casesFromSetting?: BranchCasesFromSetting;
  /** Immer angehängte Fälle (z.B. ein impliziter „Sonst"-Zweig am Ende). */
  appendCases?: { key?: string; label?: string }[];
}

/** Trennzeichen einer als Freitext gepflegten Optionsliste — identisch zu shared/ai_options.py. */
const OPTION_SPLIT = /[,;\n|]+/;

/** Optionsliste aus einem Array ODER einem Trennzeichen-String lesen (Pendant zu `parse_options`). */
export function parseOptions(raw: any): string[] {
  if (raw === null || raw === undefined) return [];
  const values = Array.isArray(raw) ? raw : String(raw).split(OPTION_SPLIT);
  return values.map(v => (v ?? '').toString().trim()).filter(v => !!v);
}

/**
 * Fälle aus der Nutzer-Konfiguration ableiten.
 *
 * Die Schlüssel entstehen über {@link slugKey} aus dem Anzeigelabel — dieselbe Regel wie in
 * `shared.ai_options.slug_key` (Python) und `BranchDecision.slugify` (Java). Dadurch sind
 * Groß-/Kleinschreibung, Umlaute in der Anzeige und Leerzeichen nicht kantenrelevant; nur eine
 * echte inhaltliche Umbenennung erzeugt einen neuen Schlüssel und damit einen neuen Port.
 */
export function resolveDynamicCases(
  spec: BranchCasesFromSetting | undefined,
  settings?: { [key: string]: any },
): { cases: BranchCase[]; outputKey?: string } {
  if (!spec?.path) return {cases: []};
  const items = Array.isArray(settings?.[spec.path]) ? settings![spec.path] : [];
  const matching = spec.where
    ? items.filter((item: any) => item?.[spec.where!.property] === spec.where!.equals)
    : items;

  const used = new Set<string>();
  const cases: BranchCase[] = [];
  let outputKey: string | undefined;

  for (const item of matching) {
    if (spec.sourceKeyFrom && !outputKey) {
      const key = (item?.[spec.sourceKeyFrom] ?? '').toString().trim();
      if (key) outputKey = key;
    }
    const labels = spec.optionsProperty
      ? parseOptions(item?.[spec.optionsProperty])
      : [(item?.[spec.labelProperty ?? 'name'] ?? '').toString().trim()];
    for (const label of labels) {
      if (!label) continue;
      cases.push({key: slugKey(label, used), label});
    }
  }
  return {cases, outputKey};
}

/**
 * Resolve how a node branches — the single source of truth for flow-out ports, edge sync and
 * edge derivation.
 *
 * Note the `settings` argument: a module can make its branching conditional on a setting
 * (`x-branch.enabledBySetting`, e.g. KI-Anweisung's Prüfmodus), so branching is a property of the
 * NODE, not of the module alone. Callers that omit `settings` get the unconditional answer, which
 * is correct for modules whose branching is static.
 */
export function resolveBranchSpec(
  mod?: ModuleManifest | null,
  settings?: { [key: string]: any },
): BranchSpec | null {
  const properties = (mod?.outputSchema as { properties?: Record<string, any> } | undefined)?.properties;

  for (const [outputKey, prop] of Object.entries(properties ?? {})) {
    const branch = prop?.['x-branch'] as BranchExtension | undefined;
    if (!branch) continue;
    // Beide Gates überspringen nur DIESE Property (`continue`), sie beenden nicht die Suche.
    // Vorher stand hier `return null`, was die erste stillgelegte Verzweigung zur letzten machte —
    // damit blieb die Auswahl-Verzweigung der KI-Anweisung unsichtbar, solange der Prüfmodus aus
    // war (denn `pruefergebnis` steht davor und ist dann abgeschaltet). Für die bisherigen Module
    // ist der Wechsel verhaltensgleich: keines kombiniert `enabledBySetting` mit `aktiver_pfad`.
    if (branch.enabledBySetting && !settings?.[branch.enabledBySetting]) {
      continue;
    }
    if (branch.disabledBySetting && settings?.[branch.disabledBySetting]) {
      continue;
    }

    // Dynamische Fälle haben Vorrang; die statische Liste bleibt der Fallback.
    const dynamic = resolveDynamicCases(branch.casesFromSetting, settings);
    const cases: BranchCase[] = dynamic.cases.length
      ? dynamic.cases
      : (branch.cases ?? [])
        .map(c => ({key: (c?.key ?? '').trim(), label: (c?.label ?? '').trim() || (c?.key ?? '').trim()}))
        .filter(c => !!c.key);

    if (!cases.length) continue; // nichts konfiguriert → keine Verzweigung an dieser Property

    // appendCases nur, wenn überhaupt verzweigt wird (sonst hinge ein einsamer „Sonst"-Port da).
    const appended = (branch.appendCases ?? [])
      .map(c => ({key: (c?.key ?? '').trim(), label: (c?.label ?? '').trim() || (c?.key ?? '').trim()}))
      .filter(c => !!c.key && !cases.some(existing => existing.key === c.key));
    const allCases = [...cases, ...appended];

    if (allCases.length > 1) {
      return {outputKey: dynamic.outputKey ?? outputKey, cases: allCases};
    }
  }

  if (properties?.['aktiver_pfad']) {
    return {
      outputKey: 'aktiver_pfad',
      legacy: 'thenElse',
      cases: [{key: 'dann', label: 'Dann'}, {key: 'sonst', label: 'Sonst'}],
    };
  }
  return null;
}

/** True when a node branches the flow (see {@link resolveBranchSpec}). */
export function isBranchingModule(mod?: ModuleManifest | null, settings?: { [key: string]: any }): boolean {
  return !!resolveBranchSpec(mod, settings);
}

/** Flow-out ports for a node: one per branch case, or a single ▶ when it does not branch. */
export function buildFlowOutPorts(mod?: ModuleManifest | null, settings?: { [key: string]: any }): NodePort[] {
  const spec = resolveBranchSpec(mod, settings);
  if (spec) {
    return spec.cases.map(c => ({name: c.label, dataType: 'flow', isFlowPort: true}));
  }
  return [{...FLOW_OUT_PORT}];
}

/**
 * Alle Ausgänge eines Knotens: die Flow-Ports, und dahinter je eine beschriftete **Wert-Ausgabe**,
 * wenn das Modul das per `x-outputs-as-ports` im `outputSchema` verlangt.
 *
 * Gedacht für Module, deren Ausgaben der Nutzer selbst benennt — das Variable-Modul zeigt so jede
 * definierte Variable als eigenen Ausgang, statt sie nur im Wert-Picker zu führen. Opt-in statt
 * pauschal, weil sonst jedes Modul schlagartig für jede Ausgabe einen Punkt bekäme.
 *
 * **Reihenfolge ist Vertrag:** Flow-Ports zuerst. Die gesamte Kanten-Mathematik indiziert darüber
 * (`syncEdgesToManifest` behandelt alles ab `flowPortCount` nicht mehr als Flusskante,
 * `buildEdgesFromManifest` rechnet Zweig-Keys auf Positionen um). Wert-Ausgaben dahinter können
 * deshalb keine Flusskante erzeugen und verschieben keine bestehende.
 */
export function buildOutputPorts(mod?: ModuleManifest | null, settings?: { [key: string]: any }): NodePort[] {
  const ports = buildFlowOutPorts(mod, settings);
  const outputSchema = mod?.outputSchema as { [key: string]: any } | undefined;
  if (outputSchema?.['x-outputs-as-ports'] !== true) return ports;

  const schema = buildDataOutputSchema(mod?.moduleId, settings, outputSchema);
  for (const field of dataFieldsFromSchema(schema)) {
    ports.push({name: field.title, dataType: field.type});
  }
  return ports;
}

/**
 * Number of flow-out ports for a node. Derived from {@link buildFlowOutPorts} so the port list
 * stays the single source of truth for both rendering and the port-index math in edge
 * sync/derivation — data ports start right after these, so an inconsistency here silently shifts
 * every data edge of that node.
 */
export function flowOutPortCount(mod?: ModuleManifest | null, settings?: { [key: string]: any }): number {
  return buildFlowOutPorts(mod, settings).length;
}

/** A visual edge between two nodes on the canvas */
export interface FlowEdge {
  id: string;
  source: string;
  sourcePortIndex: number;
  target: string;
  targetPortIndex: number;
  label: string;
  type: 'success' | 'error';
}

/** Reference to a source node's output port, used to highlight where a connected input is fed from. */
export interface SourcePortRef {
  nodeId: string;
  portIndex: number;
}

/** Superset of backend CategoryEnum + additional frontend display categories */
export type ModuleCategory =
  | 'TRIGGER' | 'PLANBIC' | 'IFC' | 'FILES'
  | 'VARIABLE' | 'AI' | 'LOGIC' | 'MATH' | 'TEXT'
  | 'WORKFLOWS' | 'CHECKER' | 'AGENT' | 'ACTION'
  | 'CONTEXT' | 'INPUT' | 'QUALITY';

/** Module-category display info for the sidebar */
export interface ModuleCategoryInfo {
  category: ModuleCategory;
  label: string;
  description: string;
  color: string;
  bgColor: string;
  abbrev: string;
  icon?: IconDefinition;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Display constants
// ═══════════════════════════════════════════════════════════════════════════════

/** Backend category → frontend display category mapping */
const BACKEND_CATEGORY_MAP: Record<string, ModuleCategory> = {
  TRIGGER: 'TRIGGER',
  AGENT: 'AGENT',
  CHECKER: 'CHECKER',
  FLOW_CONTROL: 'LOGIC',
  ACTION: 'ACTION',
};

export function mapCategory(backendCategory: string): ModuleCategory {
  return BACKEND_CATEGORY_MAP[backendCategory] ?? (backendCategory as ModuleCategory);
}

/** Kategorie → Farben-Mapping */
export const CATEGORY_COLORS: Record<string, string> = {
  TRIGGER: '#F59E0B',
  PLANBIC: '#009999',
  IFC: '#3B82F6',
  FILES: '#22C55E',
  VARIABLE: '#8B5CF6',
  AI: '#A855F7',
  LOGIC: '#6366F1',
  MATH: '#10B981',
  TEXT: '#EC4899',
  WORKFLOWS: '#009999',
  CHECKER: '#EF4444',
  AGENT: '#0EA5E9',
  ACTION: '#F97316',
  CONTEXT: '#F59E0B',
  INPUT: '#0EA5E9',
  QUALITY: '#EF4444',
};

/** Kategorie → Hintergrundfarbe (light) */
export const CATEGORY_BG_COLORS: Record<ModuleCategory, string> = {
  TRIGGER: '#FEF3C7',
  PLANBIC: '#E6F7F7',
  IFC: '#DBEAFE',
  FILES: '#DCFCE7',
  VARIABLE: '#EDE9FE',
  AI: '#F3E8FF',
  LOGIC: '#E0E7FF',
  MATH: '#D1FAE5',
  TEXT: '#FCE7F3',
  WORKFLOWS: '#E6F7F7',
  CHECKER: '#FEE2E2',
  AGENT: '#E0F2FE',
  ACTION: '#FFEDD5',
  CONTEXT: '#FEF3C7',
  INPUT: '#E0F2FE',
  QUALITY: '#FEE2E2',
};

/** Kategorie → Abkürzung für Icon */
export const CATEGORY_ABBREVS: Record<string, string> = {
  TRIGGER: 'T',
  PLANBIC: 'P',
  IFC: 'I',
  FILES: 'F',
  VARIABLE: 'V',
  AI: 'AI',
  LOGIC: 'L',
  MATH: 'M',
  TEXT: 'Tx',
  WORKFLOWS: 'W',
  CHECKER: 'C',
  AGENT: 'Ag',
  ACTION: 'Ac',
  CONTEXT: 'Cx',
  INPUT: 'In',
  QUALITY: 'AR',
};

/** Kategorie → Label */
export const CATEGORY_LABELS: Record<string, string> = {
  TRIGGER: 'Trigger',
  PLANBIC: 'planBIC',
  IFC: 'IFC',
  FILES: 'Datei',
  VARIABLE: 'Variablen',
  AI: 'KI',
  LOGIC: 'Logik',
  MATH: 'Mathematik',
  TEXT: 'Text',
  WORKFLOWS: 'Workflows',
  CHECKER: 'Prüfungen',
  AGENT: 'Agenten',
  ACTION: 'Aktionen',
  CONTEXT: 'KI-Kontext',
  INPUT: 'Eingangsparameter',
  // Ohne Eintrag zeigte die Kategorie den rohen Backend-Wert „QUALITY". Die Kategorie trägt heute
  // nur das Modul `quality_check`; „All-Right Check" ist dessen Produktname.
  QUALITY: 'All-Right Check',
};

/** Kategorie → FontAwesome-Icon */
export const CATEGORY_ICONS: Record<string, IconDefinition | null> = {
  TRIGGER: faPlay,
  PLANBIC: faCloud,
  IFC: faIfc as IconDefinition,
  FILES: faFile,
  VARIABLE: faInputNumeric,
  AI: faSparkles,
  LOGIC: faCirclesOverlap,
  MATH: faCalculatorSimple,
  TEXT: faTextSize,
  WORKFLOWS: faCloud,
  CHECKER: null,
  AGENT: null,
  ACTION: null,
  CONTEXT: faBookOpen,
  INPUT: faArrowRightToBracket,
  QUALITY: null,
};

/**
 * FontAwesome icon name (as declared in a module manifest's `icon` field, e.g.
 * `"circles-overlap"`) → icon definition. Modules declare the kebab-case name exactly
 * as it appears on fontawesome.com; the frontend resolves it here.
 */
const MODULE_ICONS: Record<string, IconDefinition> = {
  'briefcase-arrow-right': faBriefcaseArrowRight,
  'circles-overlap': faCirclesOverlap,
  'text-size': faTextSize,
  'calculator-simple': faCalculatorSimple,
  'input-numeric': faInputNumeric,
  'sparkles': faSparkles,
  'folder-tree': faFolderTree,
  'file': faFile,
  'filter': faFilter,
  'list-check': faListCheck,
};

/** Resolves a module manifest's `icon` (FontAwesome kebab-case name) to its icon definition. */
export function resolveModuleIcon(icon?: string | null): IconDefinition | null {
  return icon ? MODULE_ICONS[icon] ?? null : null;
}

/** Kategorie → Beschreibung */
export const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  TRIGGER: 'Trigger zum Auslösen von BIC-Workflows',
  PLANBIC: 'Interaktion mit der planBIC Plattform',
  IFC: 'Analyse und Prüfung von IFC-Modellen',
  FILES: 'Öffnen und Formatieren von Dateien',
  VARIABLE: 'Variablen mit verschiedenen Datentypen',
  AI: 'Integration von Künstlicher Intelligenz',
  LOGIC: 'Logische Operatoren und Funktionen',
  MATH: 'Mathematische Operatoren und Funktionen',
  TEXT: 'Vergleich von Texten',
  WORKFLOWS: 'Interaktion mit anderen Workflows',
  CHECKER: 'Automatisierte Prüfungen und Validierungen',
  AGENT: 'Datenextraktion und intelligente Verarbeitung',
  ACTION: 'Berechnungen und Transformationen',
  CONTEXT: 'Hintergrundinformationen für KI-Modelle',
  INPUT: 'Definition notwendiger Eingabewerte',
  QUALITY: 'Prüfung nach ASR, DIN, MBO, LBO und FaBIM',
};

/**
 * Prüfregel-Module, die zwar in der Registry als `PRUEFREGEL` geführt werden (sie müssen laufen
 * können, wo bereits vorhandene Prüfregeln – insbesondere vorgefertigte myChecks – sie
 * referenzieren), aber normale Nutzer nicht selbst in einer neuen hybriden Prüfregel verdrahten
 * sollen. Wirkt NUR auf die Modul-Palette (Drag&Drop-Liste); {@code allModules} bleibt
 * unangetastet, damit bestehende Knoten weiter aufgelöst, angezeigt und ausgeführt werden.
 *
 * - `bim_basis_check`: nur über die vorgefertigten myChecks nutzbar, nicht frei kombinierbar.
 */
export const PALETTE_HIDDEN_MODULE_IDS: ReadonlySet<string> = new Set(['bim_basis_check']);

/**
 * Build ModuleCategoryInfo[] dynamically from the actual modules returned by the backend.
 * Only categories that have at least one module will be included.
 * Uses the static lookup maps (CATEGORY_LABELS, CATEGORY_COLORS, etc.) for display info.
 *
 * @param modules All modules from the backend
 * @param moduleType Optional filter: 'WORKFLOW' or 'PRUEFREGEL'. If set, only modules with that moduleType are considered.
 */
export function buildCategoriesFromModules(modules: ModuleManifest[], moduleType?: string): ModuleCategoryInfo[] {
  const filtered = moduleType
    ? modules.filter(m => m.moduleType === moduleType)
    : modules;

  const seen = new Set<ModuleCategory>();
  const ordered: ModuleCategory[] = [];

  for (const mod of filtered) {
    const cat = mapCategory(mod.category);
    if (!seen.has(cat)) {
      seen.add(cat);
      ordered.push(cat);
    }
  }

  return ordered.map(cat => ({
    category: cat,
    label: CATEGORY_LABELS[cat] ?? cat,
    description: CATEGORY_DESCRIPTIONS[cat] ?? '',
    color: CATEGORY_COLORS[cat] ?? '#6B7280',
    bgColor: CATEGORY_BG_COLORS[cat] ?? '#F3F4F6',
    abbrev: CATEGORY_ABBREVS[cat] ?? cat.substring(0, 2),
    icon: CATEGORY_ICONS[cat] ?? undefined,
  }));
}

/** Alle Kategorien für die Seitenleiste (statischer Fallback) */
export const MODULE_CATEGORIES: ModuleCategoryInfo[] = [
  {
    category: 'TRIGGER',
    label: 'Trigger',
    description: 'Trigger zum Auslösen von BIC-Workflows',
    color: '#F59E0B',
    bgColor: '#FEF3C7',
    abbrev: 'T',
    icon: faPlay
  },
  {
    category: 'PLANBIC',
    label: 'planBIC',
    description: 'Interaktion mit der planBIC Plattform',
    color: '#009999',
    bgColor: '#E6F7F7',
    abbrev: 'P',
    icon: faCloud
  },
  {
    category: 'WORKFLOWS',
    label: 'Workflows',
    description: 'Interaktion mit anderen Workflows',
    color: '#009999',
    bgColor: '#E6F7F7',
    abbrev: 'W',
    icon: faCloud
  },
  {
    category: 'FILES',
    label: 'Datei',
    description: 'Öffnen und Formatieren von Dateien',
    color: '#22C55E',
    bgColor: '#DCFCE7',
    abbrev: 'F',
    icon: faFile
  },
  {
    category: 'VARIABLE',
    label: 'Variablen',
    description: 'Variablen mit verschiedenen Datentypen',
    color: '#8B5CF6',
    bgColor: '#EDE9FE',
    abbrev: 'V',
    icon: faBracketsCurly
  },
  {
    category: 'AI',
    label: 'KI',
    description: 'Integration von Künstlicher Intelligenz',
    color: '#A855F7',
    bgColor: '#F3E8FF',
    abbrev: 'AI',
    icon: faSparkles
  },
  {
    category: 'LOGIC',
    label: 'Logik',
    description: 'Logische Operatoren und Funktionen',
    color: '#6366F1',
    bgColor: '#E0E7FF',
    abbrev: 'L',
    icon: faCirclesOverlap
  },
  {
    category: 'MATH',
    label: 'Mathematik',
    description: 'Mathematische Operatoren und Funktionen',
    color: '#10B981',
    bgColor: '#D1FAE5',
    abbrev: 'M',
    icon: faCalculatorSimple
  },
  {
    category: 'TEXT',
    label: 'Text',
    description: 'Vergleich von Texten',
    color: '#EC4899',
    bgColor: '#FCE7F3',
    abbrev: 'Tx',
    icon: faTextSize
  },
  {
    category: 'IFC',
    label: 'IFC',
    description: 'Analyse und Prüfung von IFC-Modellen',
    color: '#3B82F6',
    bgColor: '#DBEAFE',
    abbrev: 'I',
    icon: faIfc as IconDefinition
  },
  {
    category: 'CHECKER',
    label: 'Prüfungen',
    description: 'Automatisierte Prüfungen und Validierungen',
    color: '#EF4444',
    bgColor: '#FEE2E2',
    abbrev: 'C'
  },
  {
    category: 'AGENT',
    label: 'Agenten',
    description: 'Datenextraktion und intelligente Verarbeitung',
    color: '#0EA5E9',
    bgColor: '#E0F2FE',
    abbrev: 'Ag'
  },
  {
    category: 'ACTION',
    label: 'Aktionen',
    description: 'Berechnungen und Transformationen',
    color: '#F97316',
    bgColor: '#FFEDD5',
    abbrev: 'Ac'
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// Utility functions for working with backend models
// ═══════════════════════════════════════════════════════════════════════════════

/** True when an input value is a `${nodeId.outputs.field}` graph reference rather than a literal. */
export function isVariableReference(value: unknown): boolean {
  return typeof value === 'string' && value.startsWith('${') && value.endsWith('}');
}

/**
 * Die konfigurierbaren bzw. adressierbaren Schlüssel eines Schemas.
 *
 * Übersprungen wird `x-hidden`: das Feld ist Teil des Schemas, aber weder im Panel pflegbar noch
 * als Wert anwählbar (z.B. der `aktiver_pfad` eines Verzweigungsmoduls, der die Flow-Ausgänge
 * speist). `x-port-hidden` stammt aus der Zeit der Datenports und filtert weiterhin
 * **Ausgabe**-Schemata, damit ein bewusst nicht angebotener Wert nicht im Picker auftaucht.
 */
export function visibleSchemaKeys(schema?: { [key: string]: any }): string[] {
  const props = schema?.properties;
  if (!props || typeof props !== 'object') return [];
  return Object.keys(props).filter(key =>
    props[key]?.['x-hidden'] !== true && props[key]?.['x-port-hidden'] !== true);
}

// ═══════════════════════════════════════════════════════════════════════════════
// Wertbindung: Festwert oder Referenz auf die Ausgabe eines vorherigen Schritts
// ───────────────────────────────────────────────────────────────────────────────
// Ein Eingang wird nicht mehr per Kante verdrahtet, sondern im Konfig-Panel gebunden. Die
// Persistenz bleibt dabei EXAKT wie zuvor: `inputs[key] = "${nodeId.outputs.key}"`. Damit ist das
// Backend unberührt — Entity-Hydrierung, Claim-Check und `resolveInputs` arbeiten weiter auf
// derselben Form. Alles hier ist reine Funktion und deshalb ohne Browser testbar.
// ═══════════════════════════════════════════════════════════════════════════════

/** Ein Datenfeld eines Schemas — Ersatz für `portsFromSchema`, mit Key UND Titel. */
export interface DataField {
  key: string;
  title: string;
  type: string | string[];
  prop: any;
}

/**
 * Die konfigurierbaren Datenfelder eines Schemas.
 *
 * Anders als das frühere `portsFromSchema` liefert das den Schlüssel mit — ohne Ports gibt es
 * keinen Index mehr, über den man ihn nachträglich ermitteln könnte.
 */
export function dataFieldsFromSchema(schema?: { [key: string]: any }): DataField[] {
  const props = schema?.properties;
  if (!props || typeof props !== 'object') return [];
  return visibleSchemaKeys(schema).map(key => ({
    key,
    title: (props[key]?.title ?? key).toString(),
    type: props[key]?.type ?? 'any',
    prop: props[key],
  }));
}

/** Zerlegt `${nodeId.outputs.key}` in seine Teile. `null`, wenn es keine solche Referenz ist. */
export function parseVariableReference(value: unknown): { nodeId: string; key: string } | null {
  if (typeof value !== 'string') return null;
  // Der Key darf Punkte enthalten (`${n.outputs.a.b}` adressiert einen Pfad im Output).
  const match = /^\$\{([^.}]+)\.outputs\.(.+)}$/.exec(value.trim());
  if (!match) return null;
  const nodeId = match[1].trim();
  const key = match[2].trim();
  return nodeId && key ? {nodeId, key} : null;
}

/** Eine aufgelöste Referenz — was das Panel über die Quelle eines Werts anzeigen kann. */
export interface ResolvedRef {
  nodeId: string;
  nodeName: string;
  key: string;
  name: string;
  qualifiedName: string;
  options?: string[];
  /** True, wenn der Knoten oder der Schlüssel im Manifest nicht (mehr) existiert. */
  dangling: boolean;
}

/**
 * Löst `${nodeId.outputs.key}` gegen das Manifest auf: Knotenname, Anzeigename der Ausgabe und
 * deren mögliche Werte.
 *
 * **Bewusst unabhängig von {@link upstreamVariables}.** Eine Bestandsreferenz kann auf einen Knoten
 * zeigen, der kein Fluss-Vorgänger ist — der Picker bietet sie dann nicht mehr an, aber angezeigt
 * werden muss sie trotzdem. Wäre die Anzeige an die Kandidatenliste gekoppelt, verschwänden solche
 * Bindungen stillschweigend aus der Oberfläche, während sie zur Laufzeit weiterwirken.
 */
export function resolveVariableReference(
  manifest: Manifest | null | undefined,
  modules: ModuleManifest[],
  ref: unknown,
): ResolvedRef | null {
  const parsed = parseVariableReference(ref);
  if (!parsed) return null;

  const moduleById = new Map((modules ?? []).map(m => [m.moduleId, m]));
  const trigger = manifest?.trigger;
  const step = (manifest?.steps ?? []).find(s => s.nodeId === parsed.nodeId);

  let schema: { [key: string]: any } | undefined;
  let nodeName = parsed.nodeId;

  if (trigger?.nodeId === parsed.nodeId) {
    const mod = moduleById.get(trigger.moduleId ?? '');
    // Wie beim Rendering: das dynamisch gebaute config.outputSchema gewinnt (Eingangsparameter —
    // ein Feld je Set-Parameter, mit dem Parameternamen als Titel).
    schema = (trigger.config?.['outputSchema'] as any)
      ?? ((trigger.moduleId ?? '').toLowerCase() === 'eingangsparameter'
        ? buildParameterOutputSchema(trigger.config ?? {})
        : mod?.outputSchema);
    nodeName = getNodeName(trigger.config, mod);
  } else if (step) {
    const mod = moduleById.get(step.moduleId ?? '');
    schema = buildDataOutputSchema(step.moduleId, step.settings, mod?.outputSchema);
    nodeName = getNodeName(step.inputs, mod);
  } else {
    return {
      nodeId: parsed.nodeId, nodeName: parsed.nodeId, key: parsed.key,
      name: parsed.key, qualifiedName: `${parsed.nodeId}.${parsed.key}`, dangling: true,
    };
  }

  const prop = schema?.['properties']?.[parsed.key];
  const name = (prop?.title ?? parsed.key).toString();
  const options = Array.isArray(prop?.enum)
    ? prop.enum.map((o: any) => String(o)).filter((o: string) => !!o)
    : undefined;

  return {
    nodeId: parsed.nodeId,
    nodeName,
    key: parsed.key,
    name,
    qualifiedName: `${nodeName}.${name}`,
    options: options?.length ? options : undefined,
    dangling: !prop,
  };
}

/** Bindungszustand eines Eingangs. */
export interface InputBinding {
  kind: 'none' | 'literal' | 'reference';
  raw: any;
  /** Nur bei `reference`: der rohe `${…}`-String. */
  ref?: string;
}

/**
 * Bindungszustand eines Eingangs aus dem gespeicherten `inputs`-Objekt.
 *
 * **`0` und `false` sind Festwerte, nicht „leer".** Eine naive Falsy-Prüfung würde eine gültige
 * Bindung auf `0` als fehlend melden und damit das Speichern blockieren.
 */
export function inputBindingOf(inputs: { [key: string]: any } | null | undefined, key: string): InputBinding {
  const raw = inputs?.[key];
  if (isVariableReference(raw)) return {kind: 'reference', raw, ref: raw as string};
  if (raw === undefined || raw === null || raw === '') return {kind: 'none', raw};
  // Leere Sammelwerte gelten als unbesetzt: eine leere Map hat keinen Eintrag, ein Array aus
  // lauter `null` (Formel-Slots) trägt keinen Wert.
  if (Array.isArray(raw)) {
    return raw.some(v => v !== null && v !== undefined && v !== '')
      ? {kind: 'literal', raw} : {kind: 'none', raw};
  }
  if (typeof raw === 'object') {
    return Object.keys(raw).length ? {kind: 'literal', raw} : {kind: 'none', raw};
  }
  return {kind: 'literal', raw};
}

/** Festwert setzen (leerer Wert löscht den Schlüssel, damit `inputs` nicht zumüllt). */
export function setInputLiteral(
  inputs: { [key: string]: any },
  key: string,
  value: any,
  prop?: { [key: string]: any },
): void {
  if (value === undefined || value === null || value === '') {
    delete inputs[key];
    return;
  }
  inputs[key] = coerceLiteral(value, prop);
}

/** Referenz auf die Ausgabe eines vorherigen Schritts setzen. */
export function setInputReference(inputs: { [key: string]: any }, key: string, ref: string): void {
  inputs[key] = ref;
}

/** Bindung entfernen. */
export function clearInput(inputs: { [key: string]: any }, key: string): void {
  delete inputs[key];
}

/** Die Var-Slots des Formel-Moduls (Anzahl folgt `settings.input_variablen`). */
export function formelSlots(settings?: { [key: string]: any }): { index: number; label: string }[] {
  return Array.from({length: formelVarCount(settings)}, (_, index) => ({
    index,
    label: `Var${index + 1}`,
  }));
}

/** Aktuelle Werte der Formel-Slots (positionsgetreu, Lücken als `null`). */
export function formelSlotValues(inputs?: { [key: string]: any }): any[] {
  const raw = inputs?.['variablen'];
  return Array.isArray(raw) ? [...raw] : [];
}

/**
 * Einen Formel-Slot setzen oder leeren.
 *
 * Die Position trägt die Bedeutung (`Var1` = `variablen[0]`), deshalb wird bis zum Index mit `null`
 * aufgefüllt statt zu verdichten. Sind am Ende alle Slots leer, verschwindet der Schlüssel ganz —
 * sonst bliebe ein `[null, null]` zurück, das `inputBindingOf` zwar als leer erkennt, das aber
 * unnötig im Manifest steht.
 */
export function setFormelSlot(inputs: { [key: string]: any }, index: number, value: any): void {
  const slots = formelSlotValues(inputs);
  while (slots.length <= index) slots.push(null);
  slots[index] = (value === undefined || value === '') ? null : value;

  if (slots.every(v => v === null || v === undefined || v === '')) {
    delete inputs['variablen'];
    return;
  }
  inputs['variablen'] = slots;
}

/**
 * Alle `${…}`-Referenzen in einem `inputs`-Objekt auf neue Knoten-IDs umschreiben (Einfügen von
 * kopierten Knoten).
 *
 * Rekursiv über Strings, Listen UND Maps: eine Referenz kann in einem Formel-Array oder in einer
 * Variablen-Map stecken. Der frühere Einfüge-Pfad schrieb nur skalare Referenzen um und ließ
 * Formel-Slots und Maps auf die Originalknoten zeigen.
 */
export function remapReferences(inputs: { [key: string]: any } | null | undefined,
                                oldToNewId: Map<string, string>): void {
  if (!inputs) return;

  const remap = (value: any): any => {
    const parsed = parseVariableReference(value);
    if (parsed) {
      const newId = oldToNewId.get(parsed.nodeId);
      return newId ? `\${${newId}.outputs.${parsed.key}}` : value;
    }
    if (Array.isArray(value)) return value.map(remap);
    if (value && typeof value === 'object') {
      const out: { [key: string]: any } = {};
      for (const [k, v] of Object.entries(value)) out[k] = remap(v);
      return out;
    }
    return value;
  };

  for (const key of Object.keys(inputs)) {
    inputs[key] = remap(inputs[key]);
  }
}

/**
 * Pflicht-Eingänge ohne Wert.
 *
 * „Hat einen Wert" heißt: Festwert ODER Referenz auf einen vorherigen Schritt. Kanten spielen keine
 * Rolle mehr — die Bindung steht in `inputs`. Bewusst über {@link visibleSchemaKeys} (nicht
 * `Object.keys`), damit `x-hidden`-Felder, die der Nutzer gar nicht befüllen kann, nie als fehlend
 * gemeldet werden.
 */
export function collectMissingInputs(manifest: Manifest, modules: ModuleManifest[]): string[] {
  const errors: string[] = [];
  const moduleById = new Map((modules ?? []).map(m => [m.moduleId, m]));

  for (const step of manifest?.steps ?? []) {
    const mod = moduleById.get(step.moduleId ?? '');
    const schema: any = mod?.inputSchema;
    const required: string[] = Array.isArray(schema?.required) ? schema.required : [];
    if (!required.length) continue;

    const props = schema?.properties ?? {};
    const configurable = new Set(visibleSchemaKeys(schema));
    const stepLabel = getNodeName(step.inputs, mod);

    for (const key of required) {
      if (!configurable.has(key)) continue; // x-hidden: nicht befüllbar, also kein Fehler
      if (inputBindingOf(step.inputs, key).kind !== 'none') continue;
      const label = props[key]?.title ?? key;
      errors.push(`Schritt "${stepLabel}": Eingang "${label}" hat keinen Wert.`);
    }
  }
  return errors;
}

/**
 * Leer im Sinne einer Pflichtangabe: fehlt, leerer/Whitespace-String, leere Liste – oder ein
 * IDS-Wertfeld (`x-widget: ids-value`), dessen gewählte Art keinen Inhalt hat (wie im IDS-Editor).
 */
function isEmptySettingValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (isValueField(value)) return isValueFieldEmpty(value);
  return false;
}

/** Feldtitel ohne das Pflicht-Sternchen der Manifeste („Name*" → „Name"). */
function settingLabel(prop: any, key: string): string {
  return String(prop?.title ?? key).replace(/\s*\*\s*$/, '');
}

/** Werte mit den Schema-Defaults aufgefüllt – der Editor zeigt Defaults an, speichert sie aber nicht. */
function withDefaults(schema: any, values: any): { [key: string]: any } {
  const filled: { [key: string]: any } = {};
  for (const [key, prop] of Object.entries<any>(schema?.properties ?? {})) {
    if (prop && 'default' in prop) filled[key] = prop.default;
  }
  return {...filled, ...(values && typeof values === 'object' ? values : {})};
}

/**
 * Unvollständige Einstellungen – das Gegenstück zu {@link collectMissingInputs} für `settings`.
 *
 * Geprüft wird nur, was das Manifest ausdrücklich verlangt, und nur für Felder, die der Nutzer
 * gerade sieht (`x-visible-when` gegen die Einstellungen bzw. gegen den Listeneintrag):
 * - `required` auf Schema-Ebene: Wert fehlt und es gibt keinen Default.
 * - `x-required` am Feld: Pflicht, solange es sichtbar ist – auch in Listeneinträgen. Für Felder,
 *   die je nach Facette/Prüfmodus Pflicht sind; das kann `required` nicht ausdrücken.
 * - `minItems` an Listen.
 *
 * Ohne diese Prüfung fiel z.B. eine Relationsprüfung ohne IFC-Klasse erst im Modul auf – als
 * technisch fehlgeschlagener Testlauf.
 */
export function collectMissingSettings(manifest: Manifest, modules: ModuleManifest[]): string[] {
  const errors: string[] = [];
  const moduleById = new Map((modules ?? []).map(m => [m.moduleId, m]));

  for (const step of manifest?.steps ?? []) {
    const mod = moduleById.get(step.moduleId ?? '');
    const schema: any = mod?.settingsSchema;
    if (!schema?.properties) continue;

    const settings = withDefaults(schema, step.settings);
    const required = new Set<string>(Array.isArray(schema.required) ? schema.required : []);
    const stepLabel = getNodeName(step.inputs, mod);

    for (const key of visibleSchemaKeys(schema)) {
      const prop = schema.properties[key];
      if (!matchesVisibleWhen(prop, settings)) continue;
      const label = settingLabel(prop, key);
      const value = settings[key];

      // `required` bei Listen heißt nur „Schlüssel vorhanden" – eine leere Liste ist dort erlaubt
      // (Bedingungen, Variablen). Wie viele Einträge nötig sind, sagt allein `minItems`.
      const mustHaveValue = prop?.['x-required'] === true || (required.has(key) && prop?.type !== 'array');
      if (mustHaveValue && isEmptySettingValue(value)) {
        errors.push(`Schritt "${stepLabel}": "${label}" fehlt.`);
        continue;
      }
      const minItems = Number(prop?.minItems ?? 0);
      if (prop?.type === 'array' && minItems > 0 && (Array.isArray(value) ? value.length : 0) < minItems) {
        errors.push(minItems === 1
          ? `Schritt "${stepLabel}": "${label}" braucht mindestens einen Eintrag.`
          : `Schritt "${stepLabel}": "${label}" braucht mindestens ${minItems} Einträge.`);
        continue;
      }

      // Listeneinträge: x-required-Felder je Eintrag, sichtbar gegen den Eintrag selbst.
      const itemSchema = prop?.items;
      if (!Array.isArray(value) || !itemSchema?.properties) continue;
      value.forEach((rawItem, index) => {
        const item = withDefaults(itemSchema, rawItem);
        for (const itemKey of visibleSchemaKeys(itemSchema)) {
          const itemProp = itemSchema.properties[itemKey];
          if (itemProp?.['x-required'] !== true || !matchesVisibleWhen(itemProp, item)) continue;
          if (!isEmptySettingValue(item[itemKey])) continue;
          errors.push(`Schritt "${stepLabel}": "${label}", Eintrag ${index + 1}: "${settingLabel(itemProp, itemKey)}" fehlt.`);
        }
      });
    }
  }
  return errors;
}

/** Vorlage nach Einstellung wählen – dieselbe Form wie `x-card-title-template` (`by`/`templates`). */
function pickTemplate(spec: any, settings: { [key: string]: any }): string | null {
  if (typeof spec === 'string') return spec;
  if (!spec || typeof spec !== 'object') return null;
  const chosen = spec.by ? spec.templates?.[settings?.[spec.by]] : spec.template;
  const text = chosen ?? spec.default;
  return typeof text === 'string' && text.trim() ? text : null;
}

/** Ergebnis von {@link applyUpstreamPrefill}: was eingesetzt wurde und woher. */
export interface PrefillResult {
  /** Gefüllte Settings-Schlüssel des Zielschritts. */
  keys: string[];
  /** Anzeigename des Vorgängers, aus dem vorbelegt wurde. */
  sourceName: string | null;
}

/**
 * Belegt leere Einstellungen eines Schritts aus seinem nächsten Vorgänger vor – rein über das
 * Manifest, ohne Modul-Sonderfälle:
 *
 * - `x-prefill: "verdict"` am Ziel-Feld → Bindung an die Ausgabe mit `x-role: "verdict"` (das
 *   Verdikt des Vorgängers, z.B. IFC-Prüfung → Prüfstatus). Gegen die Werte des Ziel-`enum`
 *   geprüft, damit nie ein Status entsteht, den das Ziel ablehnt.
 * - `x-prefill: "answer-template"` am Ziel-Feld → der Text aus `x-answer-template` im
 *   Ausgabeschema des Vorgängers, nach dessen Einstellungen gewählt (`by`/`templates`).
 *
 * Nur leere Felder werden gefüllt, damit nichts überschrieben wird, was der Nutzer schon
 * eingetragen hat. Maßgeblich ist der nächste Vorgänger im Fluss, der etwas anbietet.
 *
 * Anlass: Der Status der Nutzerantwort hinter einer IFC-Prüfung wurde an `Ergebnis`
 * (SUCCESS/…) statt an `Prüfstatus` gebunden, und der Lauf scheiterte mit „Ungültiger Status".
 */
export function applyUpstreamPrefill(manifest: Manifest, modules: ModuleManifest[], nodeId: string): PrefillResult {
  const result: PrefillResult = {keys: [], sourceName: null};
  const step = (manifest?.steps ?? []).find(s => s.nodeId === nodeId);
  if (!step) return result;
  const moduleById = new Map((modules ?? []).map(m => [m.moduleId, m]));
  const props: { [key: string]: any } = (moduleById.get(step.moduleId ?? '')?.settingsSchema as any)?.properties ?? {};
  const wanted = Object.entries(props).filter(([, prop]) => typeof prop?.['x-prefill'] === 'string');
  if (!wanted.length) return result;

  // Nächster Vorgänger zuerst (upstreamNodeIds liefert ihn zuletzt). Nur Fluss-Kanten: eine
  // bestehende Bindung soll nicht selbst zum Vorgänger machen.
  const sources = upstreamNodeIds(manifest, nodeId, false).reverse()
    .map(id => (manifest.steps ?? []).find(s => s.nodeId === id))
    .filter((s): s is StepNode => !!s);

  const settings = step.settings ?? (step.settings = {});
  for (const [key, prop] of wanted) {
    if (!isEmptySettingValue(settings[key])) continue;
    for (const source of sources) {
      const sourceModule = moduleById.get(source.moduleId ?? '');
      const outputSchema: any = sourceModule?.outputSchema ?? {};
      const sourceSettings = source.settings ?? {};
      let value: string | null = null;

      if (prop['x-prefill'] === 'verdict') {
        const allowed = Array.isArray(prop.enum) ? new Set(prop.enum.map(String)) : null;
        const verdictKey = Object.keys(outputSchema.properties ?? {}).find(outKey => {
          const out = outputSchema.properties[outKey];
          if (out?.['x-role'] !== 'verdict' || !matchesVisibleWhen(out, sourceSettings)) return false;
          const values: string[] = Array.isArray(out.enum) ? out.enum.map(String) : [];
          return !allowed || values.every(v => allowed.has(v));
        });
        if (verdictKey) value = `\${${source.nodeId}.outputs.${verdictKey}}`;
      } else if (prop['x-prefill'] === 'answer-template') {
        value = pickTemplate(outputSchema['x-answer-template'], sourceSettings);
      }

      if (value !== null) {
        settings[key] = value;
        result.keys.push(key);
        result.sourceName ??= getNodeName(source.inputs, sourceModule);
        break;
      }
    }
  }
  return result;
}

/**
 * Coerce a value typed into an unconnected input port into the type its `inputSchema`
 * property declares, so the module handler receives a number/boolean instead of a string.
 *
 * A `${…}` reference is never coerced — it stays a graph reference. Unparsable input keeps
 * its raw text rather than becoming `NaN`/`null`, so the user sees what they typed and the
 * handler can report a meaningful error. For `any`/untyped inputs a numeric-looking value
 * becomes a number (mirrors what the Formel widget has always done); everything else stays
 * a string, which the Python handlers cast via their own `data_type` (see variable._cast_value).
 */
export function coerceLiteral(value: unknown, schemaProp?: { type?: string } | null): unknown {
  if (typeof value !== 'string') return value;
  if (isVariableReference(value)) return value;

  const trimmed = value.trim();
  if (trimmed === '') return '';

  const type = schemaProp?.type;

  if (type === 'number' || type === 'integer') {
    const num = Number(trimmed.replace(',', '.'));
    return Number.isFinite(num) ? num : value;
  }
  if (type === 'boolean') {
    return trimmed.toLowerCase() === 'true';
  }
  if (type === 'array' || type === 'object') {
    try {
      return JSON.parse(trimmed);
    } catch {
      return value; // Rohtext behalten – der Handler parst JSON-Strings ebenfalls
    }
  }
  if (type === 'string') {
    return value;
  }

  // Untyped / "any": numerische Eingaben als Zahl speichern
  const num = Number(trimmed.replace(',', '.'));
  return Number.isFinite(num) ? num : value;
}

/** Module id of the formula node, whose single `variablen` array input is bound in N numbered slots. */
export const FORMEL_MODULE_ID = 'formel';
/** Fallback variable count for the formula node when the setting is missing/invalid. */
const FORMEL_DEFAULT_VAR_COUNT = 2;
const FORMEL_MIN_VAR_COUNT = 1;
const FORMEL_MAX_VAR_COUNT = 6;

/**
 * Anzahl der numerierten Variablen-Slots (Var1..VarN) des Formel-Moduls, gelesen aus dem Setting
 * `input_variablen` und auf den vom Schema erlaubten Bereich begrenzt.
 */
export function formelVarCount(settings?: { [key: string]: any }): number {
  const raw = Number(settings?.['input_variablen']);
  if (!Number.isFinite(raw)) return FORMEL_DEFAULT_VAR_COUNT;
  return Math.min(FORMEL_MAX_VAR_COUNT, Math.max(FORMEL_MIN_VAR_COUNT, Math.trunc(raw)));
}


/** Module id of the AI-instruction node whose configured Ausgabeparameter become dynamic output ports. */
export const AI_INSTRUCTION_MODULE_ID = 'ai_instruction_checker';

/**
 * Module id des terminalen Ergebnisknotens einer Prüfregel. Sein Output ist das Verdikt, das im
 * Bericht landet — ein Flow-Pfad, der nicht hier endet, liefert kein bewertbares Ergebnis.
 */
export const NUTZERANTWORT_MODULE_ID = 'nutzerantwort';

/**
 * Prüft, ob eine Hybrid-Prüfregel ausführbar und vollständig modelliert ist.
 *
 * Blockierend beim Speichern:
 * - mindestens ein Prüfschritt (irgendein Knoten außer der Nutzerantwort)
 * - mindestens ein Nutzerantwort-Knoten
 * - keine Verzweigungs-Sackgassen ohne erreichbare Nutzerantwort
 *
 * Der erste Punkt verlangte früher ausdrücklich eine **KI-Anweisung**. Das stammt aus der Zeit,
 * in der Hybrid-Prüfregeln immer KI-Regeln waren, und sperrte jede Prüfregel aus, die ohne Modell
 * auskommt — All-Right Check, BIM-Basis-Check, IFC-Prüfung. Der eigentliche Zweck bleibt: eine
 * Regel, die nichts prüft und nur eine feste Antwort ausgibt, ist keine Regel.
 */
export function collectHybridCompletenessErrors(
  manifest: Manifest,
  modules: ModuleManifest[],
  edges: FlowEdge[],
): string[] {
  const errors: string[] = [];
  const steps = manifest?.steps ?? [];
  const stepModuleIds = steps.map(step => (step.moduleId ?? '').toLowerCase());
  if (!stepModuleIds.some(moduleId => moduleId && moduleId !== NUTZERANTWORT_MODULE_ID)) {
    errors.push('Mindestens ein Prüfschritt ist erforderlich.');
  }
  if (!stepModuleIds.includes(NUTZERANTWORT_MODULE_ID)) {
    errors.push('Mindestens eine Nutzerantwort ist erforderlich.');
  }

  const moduleById = new Map((modules ?? []).map(module => [module.moduleId, module]));
  const stepById = new Map(steps.map(step => [step.nodeId, step]));
  const isTerminal = (nodeId: string) =>
    (stepById.get(nodeId)?.moduleId ?? '').toLowerCase() === NUTZERANTWORT_MODULE_ID;

  const memo = new Map<string, boolean>();
  const reachesNutzerantwort = (nodeId: string, onPath: Set<string>): boolean => {
    if (isTerminal(nodeId)) return true;
    const cached = memo.get(nodeId);
    if (cached !== undefined) return cached;
    if (onPath.has(nodeId)) return false;
    onPath.add(nodeId);
    const found = edges
      .filter(edge => edge.source === nodeId && edge.targetPortIndex === 0)
      .some(edge => reachesNutzerantwort(edge.target, onPath));
    onPath.delete(nodeId);
    memo.set(nodeId, found);
    return found;
  };

  for (const step of steps) {
    const module = moduleById.get(step.moduleId ?? '');
    const branchSpec = resolveBranchSpec(module, step.settings);
    if (!branchSpec) continue;
    const stepLabel = getNodeName(step.inputs, module);
    branchSpec.cases.forEach((branchCase, portIndex) => {
      const outgoing = edges.filter(edge =>
        edge.source === step.nodeId && edge.sourcePortIndex === portIndex && edge.targetPortIndex === 0);
      if (outgoing.length && outgoing.some(edge => reachesNutzerantwort(edge.target, new Set()))) return;
      errors.push(`Schritt "${stepLabel}": Der Zweig „${branchCase.label}" endet ohne `
        + `Nutzerantwort — dieses Ergebnis erscheint im Bericht als „unbewertet".`);
    });
  }

  return errors;
}

/**
 * `x-managed-items` auf einem object-array: Einträge, die das Modul selbst verwaltet, sobald ein
 * Setting aktiv ist. Sie werden automatisch angelegt und sind nicht editierbar/löschbar.
 *
 * Anwendungsfall: die KI-Anweisung im Prüfmodus braucht zwingend einen Ergebnis-Parameter — er
 * trägt das Verdikt, über das der Knoten verzweigt. Fehlt er, sind die WARNING-/FAILURE-Ausgänge
 * nie erreichbar.
 */
export interface ManagedItemsSpec {
  /** Setting, das die Verwaltung aktiviert. Fehlt es, gilt sie immer. */
  enabledBySetting?: string;
  /** Property, über die ein Eintrag identifiziert wird (Standard: `name`). */
  keyProperty?: string;
  /** Die verwalteten Einträge, in dieser Reihenfolge am Anfang der Liste. */
  items?: Record<string, any>[];
}

/** Liest `x-managed-items` — `null`, wenn nicht deklariert oder per Setting abgeschaltet. */
export function resolveManagedItems(
  arraySchema: { [key: string]: any } | null | undefined,
  settings?: { [key: string]: any },
): ManagedItemsSpec | null {
  const spec = arraySchema?.['x-managed-items'] as ManagedItemsSpec | undefined;
  if (!spec?.items?.length) return null;
  if (spec.enabledBySetting && !settings?.[spec.enabledBySetting]) return null;
  return spec;
}

/** Schlüsselwerte der verwalteten Einträge — die Zeilen, die der Repeater sperrt. */
export function managedItemKeys(spec: ManagedItemsSpec): string[] {
  const keyProp = spec.keyProperty ?? 'name';
  return (spec.items ?? []).map(item => String(item?.[keyProp] ?? '')).filter(k => !!k);
}

/**
 * Ergänzt fehlende verwaltete Einträge in `value` — additiv und ohne Umsortieren.
 *
 * Bewusst so zurückhaltend: der Prüfmodus ist nur eine Weiterleitung, die übrigen
 * Ausgabeparameter bleiben frei definierbar und müssen unangetastet funktionieren.
 * Deshalb gilt:
 * - Ein fehlender Eintrag wird **hinten angehängt**, nie vorn eingefügt. Der Repeater trackt
 *   Zeilen über den Index; ein Einschub an Position 0 verschiebt alle Zeilen und der Nutzer
 *   tippt anschließend in eine andere (gesperrte) Zeile weiter.
 * - Ein vorhandener Eintrag gleichen Namens wird **nicht überschrieben**, nur leere Felder
 *   werden aufgefüllt. Für den Verzweigungs-Vertrag ist das unkritisch: das Modul schreibt die
 *   Verdikt-Anweisung ohnehin selbst in den Prompt, die Beschreibung hier ist nur ein Hinweis.
 * - Ist die Verwaltung inaktiv, bleibt die Liste unverändert — die Einträge werden wieder ganz
 *   normal editierbar, statt dem Nutzer wegzubrechen.
 *
 * @returns die neue Liste, oder `null`, wenn nichts zu ändern war (kein unnötiges Neuschreiben).
 */
export function applyManagedItems(
  arraySchema: { [key: string]: any } | null | undefined,
  settings: { [key: string]: any } | undefined,
  value: any,
): any[] | null {
  const spec = resolveManagedItems(arraySchema, settings);
  if (!spec) return null;

  const keyProp = spec.keyProperty ?? 'name';
  const current = Array.isArray(value) ? value : [];
  const next = [...current];
  let changed = false;

  for (const item of spec.items ?? []) {
    const key = String(item?.[keyProp] ?? '');
    if (!key) continue;
    const index = next.findIndex(row => String(row?.[keyProp] ?? '') === key);
    if (index < 0) {
      next.push({...item});
      changed = true;
      continue;
    }
    // Nur Lücken füllen, nie Eingaben des Nutzers ersetzen.
    const filled = {...item, ...pruneEmpty(next[index])};
    if (JSON.stringify(filled) !== JSON.stringify(next[index])) {
      next[index] = filled;
      changed = true;
    }
  }

  return changed ? next : null;
}

/** Kopie ohne leere Felder — Basis für „nur Lücken füllen". */
function pruneEmpty(row: any): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [key, val] of Object.entries(row ?? {})) {
    if (val !== undefined && val !== null && val !== '') out[key] = val;
  }
  return out;
}

/**
 * Die `x-dynamic-properties`-Erweiterung, deklariert auf Schema-EBENE des `outputSchema` (nicht auf
 * einer einzelnen Property). Sie sagt: „dieses Modul liefert zusätzlich eine Property je Eintrag
 * eines Array-Settings".
 */
export interface DynamicPropertiesExtension {
  /** Array-Setting, dessen Einträge zu Properties werden. */
  fromSetting: string;
  /** Item-Feld mit dem Property-Namen (= Key = Titel). */
  nameProperty?: string;
  /** Item-Feld mit der Typangabe. */
  typeProperty?: string;
  /**
   * Item-Feld mit der Optionsliste (Auswahl-Parameter). Ist es gesetzt und der Eintrag hat
   * Optionen, bekommt die erzeugte Property sie als `enum` — daraus zeigt der Editor die
   * möglichen Werte als Badges am Platzhalter.
   */
  optionsProperty?: string;
  /** Typangabe (kleingeschrieben) → JSON-Schema-Typ. Nicht getroffene Werte werden `string`. */
  typeMap?: { [dateityp: string]: string };
}

/** Typangabe eines konfigurierten Parameters → JSON-Schema-Typ des Ports. */
function jsonTypeFrom(typ: unknown, typeMap?: { [dateityp: string]: string }): string {
  return typeMap?.[(typ ?? '').toString().trim().toLowerCase()] ?? 'string';
}

/**
 * Effektives Ausgabe-Schema eines Knotens: das statische `outputSchema` des Moduls, erweitert um
 * eine Property je Eintrag des in `x-dynamic-properties` benannten Array-Settings.
 *
 * Das spiegelt Handler, die jeden konfigurierten Parameter als eigenen Top-Level-Ergebnisschlüssel
 * zurückgeben (KI-Anweisung: `result[name] = coerced`). Früher stand hier ein `moduleId ===
 * 'ai_instruction_checker'`-Vergleich; die Deklaration liegt jetzt im Manifest, damit weitere
 * Module dieselbe Mechanik ohne Editor-Änderung nutzen können.
 *
 * `moduleId` bleibt in der Signatur, damit die Aufrufer unverändert bleiben — ausgewertet wird es
 * nicht mehr.
 */
export function buildDataOutputSchema(
  moduleId?: string,
  settings?: { [key: string]: any },
  outputSchema?: { [key: string]: any },
): { [key: string]: any } {
  const dynamic = outputSchema?.['x-dynamic-properties'] as DynamicPropertiesExtension | undefined;
  if (!dynamic?.fromSetting) return outputSchema ?? {};

  const properties: { [key: string]: any } = {...(outputSchema?.properties ?? {})};
  const items = Array.isArray(settings?.[dynamic.fromSetting]) ? settings![dynamic.fromSetting] : [];
  const nameProperty = dynamic.nameProperty ?? 'name';
  for (const item of items) {
    const name = (item?.[nameProperty] ?? '').toString().trim();
    if (!name) continue;
    // Key === Titel === Parametername: der Handler legt den Wert genau unter diesem Namen ab.
    const property: { [key: string]: any } = {
      title: name,
      type: jsonTypeFrom(dynamic.typeProperty ? item?.[dynamic.typeProperty] : undefined, dynamic.typeMap),
    };
    // Optionen als `enum` — das ist die JSON-Schema-Form, und der Editor liest sie generisch
    // (Badges am Platzhalter). Statische Enums bestehender Module funktionieren dadurch gratis mit.
    const options = dynamic.optionsProperty ? parseOptions(item?.[dynamic.optionsProperty]) : [];
    if (options.length) property['enum'] = options;
    properties[name] = property;
  }
  return {type: 'object', properties};
}

/**
 * Eine Ausgabe eines Vorgänger-Knotens, die als {Platzhalter} verfügbar ist – auch ohne Kante.
 * Spiegelbild eines Eintrags des Backend-`flowContext` (FlowContextBuilder).
 */
export interface UpstreamVariable {
  nodeId: string;
  nodeName: string;
  /** Output-Schlüssel im Ergebnis des Schritts. */
  key: string;
  /** Anzeigename (Port-Titel) – so kennt der Nutzer den Wert. */
  name: string;
  /** `<nodeName>.<name>` – der eindeutige Weg, wenn `name` mehrfach vorkommt. */
  qualifiedName: string;
  /** True, wenn `name` bei mehreren Vorgängern vorkommt. */
  ambiguous: boolean;
  /** `${nodeId.outputs.key}` – falls die Referenz doch als Kante materialisiert werden soll. */
  ref: string;
  /** Mögliche Werte (aus dem `enum` der Ausgabe), falls die Ausgabe eine Auswahl ist. */
  options?: string[];
  /**
   * JSON-Schema-`type` der Ausgabe (z.B. `'string'`, `['number','integer','boolean']`). Fehlt er,
   * gilt die Ausgabe als Joker — kompatibel mit jedem Ziel (siehe `isTypeCompatible`).
   */
  type?: string | string[];
  /**
   * `x-hide-placeholder` der Ausgabe: nicht als {Platzhalter} im Text anbieten (z.B. der Anteil 0–1
   * der IFC-Prüfung neben seiner lesbaren Prozentangabe). Als Quelle für Bindungen bleibt sie wählbar.
   */
  hideAsPlaceholder?: boolean;
}


/**
 * Nachfolger-Graph aus Flow-Kanten UND Datenreferenzen.
 *
 * Spiegel von `WorkflowManifestParser#buildSuccessorGraph` im Backend – inklusive der
 * Trigger-Kante (ohne sie wäre der Eingangsparameter-Knoten kein Vorgänger) und ohne `onError`
 * (der Fehlerpfad ist keine Ausführungs-Abhängigkeit und darf rückwärts zeigen).
 */
export function buildSuccessorGraph(manifest: Manifest, includeDataRefs = true): Map<string, Set<string>> {
  const successors = new Map<string, Set<string>>();
  const add = (source: string, target: string) => {
    if (!source || !target) return;
    if (!successors.has(source)) successors.set(source, new Set());
    successors.get(source)!.add(target);
  };

  const trigger = manifest?.trigger;
  const nodeIds = new Set<string>();
  if (trigger?.nodeId) nodeIds.add(trigger.nodeId);
  for (const step of manifest?.steps ?? []) if (step.nodeId) nodeIds.add(step.nodeId);

  if (trigger?.nodeId) {
    for (const target of decodeOnSuccess(trigger.onSuccess)) add(trigger.nodeId, target);
  }

  for (const step of manifest?.steps ?? []) {
    const source = step.nodeId ?? '';
    for (const target of decodeOnSuccess(step.onSuccess)) add(source, target);
    for (const target of decodeOnSuccess(step.onThen)) add(source, target);
    for (const target of decodeOnSuccess(step.onElse)) add(source, target);
    for (const refs of Object.values(step.onBranch ?? {})) {
      for (const target of decodeOnSuccess(refs)) add(source, target);
    }
    // Datenreferenzen: X liest Y's Ausgabe → Y ist Vorgänger von X. Rekursiv, weil eine
    // Referenz auch in einer Map stecken kann.
    //
    // `includeDataRefs = false` schaltet das ab. Nötig für den Wert-Picker: sonst wäre die
    // Kandidatenliste selbstbestätigend — ein Knoten gälte als Vorgänger, WEIL er referenziert
    // wird, und eine einmal gesetzte (womöglich falsche) Bindung hielte sich selbst am Leben.
    // Für die {Platzhalter}-Chips bleibt es an: dort ist jede vorhandene Referenz erwünscht.
    if (includeDataRefs) {
      for (const ref of collectReferencedNodeIds(step.inputs, nodeIds)) add(ref, source);
    }
  }
  return successors;
}

/** Alle in einem Wert (rekursiv) referenzierten, bekannten nodeIds aus `${nodeId.outputs.…}`. */
function collectReferencedNodeIds(value: any, knownNodeIds: Set<string>): Set<string> {
  const refs = new Set<string>();
  const walk = (current: any) => {
    if (typeof current === 'string') {
      const match = /^\$\{([^.}]+)\./.exec(current.trim());
      if (match && knownNodeIds.has(match[1].trim())) refs.add(match[1].trim());
      return;
    }
    if (Array.isArray(current)) {
      current.forEach(walk);
      return;
    }
    if (current && typeof current === 'object') {
      Object.values(current).forEach(walk);
    }
  };
  walk(value);
  return refs;
}

/**
 * Alle Knoten, die im Ablauf VOR `nodeId` liegen – in Flussreihenfolge, nächster Vorgänger zuletzt.
 *
 * Geschwister-Zweige sind bewusst nicht enthalten: zwischen ihnen verläuft keine Kante, und ein
 * Zweig, der im Lauf übersprungen wird, hat keine Werte zu bieten.
 */
export function upstreamNodeIds(manifest: Manifest, nodeId: string, includeDataRefs = true): string[] {
  if (!manifest || !nodeId) return [];

  const successors = buildSuccessorGraph(manifest, includeDataRefs);
  const predecessors = new Map<string, Set<string>>();
  successors.forEach((targets, source) => targets.forEach(target => {
    if (!predecessors.has(target)) predecessors.set(target, new Set());
    predecessors.get(target)!.add(source);
  }));

  const upstream = new Set<string>();
  const visited = new Set<string>();
  const queue = [nodeId];
  while (queue.length) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    for (const predecessor of predecessors.get(current) ?? []) {
      upstream.add(predecessor);
      queue.push(predecessor);
    }
  }
  upstream.delete(nodeId);

  // Reihenfolge = Flussreihenfolge, wie im Backend (dort computeFlatExecutionOrder).
  const order = [manifest.trigger?.nodeId ?? '', ...orderStepsByFlow(manifest).map(s => s.nodeId ?? '')];
  return [...upstream].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

/**
 * Alle Ausgaben aller Vorgänger von `nodeId` als Platzhalter-Kandidaten.
 *
 * Nutzt dieselben Schema-Builder wie das Rendering (`buildDataOutputSchema` bzw. für den Trigger
 * `buildParameterOutputSchema`) — damit stimmen Schlüssel und Titel mit dem überein, was das
 * Backend in den `flowContext` schreibt, und dynamisch konfigurierte Ausgaben (Eingangsparameter,
 * KI-Ausgabeparameter) sind automatisch dabei.
 *
 * @param opts `flowOnly: true` zählt nur Vorgänger, die über **Flow-Kanten** erreichbar sind. Das
 *   braucht der Wert-Picker: ohne diese Einschränkung wäre die Liste selbstbestätigend, weil
 *   `buildSuccessorGraph` bestehende Datenreferenzen selbst zu Kanten macht. Für die
 *   {Platzhalter}-Chips bleibt der Default richtig — dort soll jede vorhandene Bindung auftauchen.
 */
/**
 * Taugt eine Ausgabe als {Platzhalter} in einem Text? Spiegel von `FlowContextBuilder.scalarOrNull`
 * im Backend: Objekte und Listen werden nicht in den Flow-Kontext übernommen. Ohne `type` (z.B.
 * dynamische Ausgaben) wird nichts ausgeschlossen.
 */
export function isScalarOutput(type: string | string[] | undefined): boolean {
  const types = Array.isArray(type) ? type : type ? [type] : [];
  return !types.length || types.some(t => t !== 'object' && t !== 'array');
}

export function upstreamVariables(
  manifest: Manifest,
  modules: ModuleManifest[],
  nodeId: string,
  opts?: { flowOnly?: boolean },
): UpstreamVariable[] {
  const moduleById = new Map((modules ?? []).map(m => [m.moduleId, m]));
  const trigger = manifest?.trigger;
  const stepById = new Map((manifest?.steps ?? []).map(s => [s.nodeId, s]));

  const collected: UpstreamVariable[] = [];
  for (const sourceId of upstreamNodeIds(manifest, nodeId, !opts?.flowOnly)) {
    let schema: { [key: string]: any };
    let nodeName: string;
    let settings: { [key: string]: any } = {};

    if (trigger?.nodeId === sourceId) {
      const mod = moduleById.get(trigger.moduleId ?? '');
      // Eingangsparameter: die Ausgänge stehen nur in der Config.
      schema = (trigger.moduleId ?? '').toLowerCase() === 'eingangsparameter'
        ? buildParameterOutputSchema(trigger.config ?? {})
        : (mod?.outputSchema ?? {});
      nodeName = getNodeName(trigger.config, mod);
    } else {
      const step = stepById.get(sourceId);
      if (!step) continue;
      const mod = moduleById.get(step.moduleId ?? '');
      schema = buildDataOutputSchema(step.moduleId, step.settings, mod?.outputSchema);
      nodeName = getNodeName(step.inputs, mod);
      settings = step.settings ?? {};
    }

    for (const key of visibleSchemaKeys(schema)) {
      const prop = schema.properties?.[key];
      // Ausgaben, die das Modul nur in einer bestimmten Einstellung liefert (IFC-Prüfung: je
      // Prüfmodus genau ein Kennwert). Sonst stünden Werte zur Auswahl, die in diesem Knoten nie
      // entstehen — als {Platzhalter} blieben sie im Bericht wörtlich stehen.
      if (!matchesVisibleWhen(prop, settings)) continue;
      const name = (prop?.title ?? key).toString();
      const options = Array.isArray(prop?.enum)
        ? prop.enum.map((o: any) => String(o)).filter((o: string) => !!o)
        : undefined;
      collected.push({
        nodeId: sourceId,
        nodeName,
        key,
        name,
        qualifiedName: `${nodeName}.${name}`,
        ambiguous: false,
        ref: `\${${sourceId}.outputs.${key}}`,
        options: options?.length ? options : undefined,
        type: prop?.type,
        ...(prop?.['x-hide-placeholder'] === true ? {hideAsPlaceholder: true} : {}),
      });
    }
  }

  // Mehrdeutig = derselbe Anzeigename bei mehreren Vorgängern. Dann führt der Editor zur
  // qualifizierten Form, weil der kurze Name im Modul auf „nächster Vorgänger gewinnt" fällt.
  const counts = new Map<string, number>();
  for (const item of collected) counts.set(item.name, (counts.get(item.name) ?? 0) + 1);
  return collected.map(item => ({...item, ambiguous: (counts.get(item.name) ?? 0) > 1}));
}

/**
 * Einzeilige Zusammenfassung der Konfiguration eines Knotens für die Node-Karte.
 *
 * Manifest-getrieben über `x-node-summary` auf Schema-Ebene des `settings_schema`: ein
 * Template-String mit `{key}`-Platzhaltern, die gegen Settings UND Inputs aufgelöst werden
 * (beim Wertevergleich stehen die Operanden in den Inputs, der Operator in den Settings).
 *
 * Bewusst kein Per-Modul-Code im Editor: welche Felder eine Karte aussagekräftig machen, weiß nur
 * das Modul.
 *
 * Drei Formen, die ein Template nutzen kann:
 *
 * * `{key}` – ein Skalar. Zeigt bei einem `enum` das Label aus `x-enumLabels` statt des
 *   technischen Werts („Erfüllt" statt `erfuellt`).
 * * `{key.sub}` – eine Liste von Einträgen: die `sub`-Property jedes Eintrags, kommagetrennt.
 *   Damit fasst das Bedingungs-Modul seine Fälle zusammen („2,50 m, 2,70 m, 3,00 m").
 * * `a · b` – mehrere Abschnitte. **Jeder Abschnitt wird für sich aufgelöst**; wo ein Platzhalter
 *   fehlt, entfällt nur dieser Abschnitt. So zeigt die Nutzerantwort ihren Status auch dann, wenn
 *   der Antworttext noch leer ist — eine halbe Zeile innerhalb eines Abschnitts („zwischen  und ")
 *   bleibt trotzdem ausgeschlossen, dieser Abschnitt entfällt komplett.
 *
 * Ein `${…}`-Wert ist eine Kantenreferenz und kein Text; dafür wird der Titel des Quell-Ports
 * eingesetzt, sofern er mitgegeben wird.
 */
export function nodeSummary(
  step: StepNode | null | undefined,
  mod: ModuleManifest | null | undefined,
  refLabel?: (ref: string) => string | null,
): string | null {
  const template = (mod?.settingsSchema as any)?.['x-node-summary'];
  if (typeof template !== 'string' || !template.trim()) return null;

  const resolved = template
    .split(SUMMARY_SEGMENT_SEPARATOR)
    .map(segment => resolveSummarySegment(segment, step, mod, refLabel))
    .filter((segment): segment is string => !!segment);

  return resolved.length ? resolved.join(SUMMARY_SEGMENT_SEPARATOR) : null;
}

/** Trennt die eigenständig auflösbaren Abschnitte eines `x-node-summary`-Templates. */
const SUMMARY_SEGMENT_SEPARATOR = ' · ';

/** Ein Abschnitt eines Summary-Templates — vollständig aufgelöst oder gar nicht. */
function resolveSummarySegment(
  segment: string,
  step: StepNode | null | undefined,
  mod: ModuleManifest | null | undefined,
  refLabel?: (ref: string) => string | null,
): string | null {
  const settings = step?.settings ?? {};
  const inputs = step?.inputs ?? {};
  let incomplete = false;

  const text = segment.replace(/\{([^{}]+)\}/g, (_match, rawKey: string) => {
    const [key, itemKey] = rawKey.trim().split('.');
    const value = settings[key] ?? inputs[key];

    // Liste von Einträgen: die genannte Property jedes Eintrags, kommagetrennt.
    if (itemKey) {
      const items = Array.isArray(value)
        ? value.map(item => String((item as any)?.[itemKey] ?? '').trim()).filter(Boolean)
        : [];
      if (!items.length) incomplete = true;
      return items.join(', ');
    }

    if (value === undefined || value === null || value === '') {
      incomplete = true;
      return '';
    }
    if (typeof value === 'string' && value.startsWith('${')) {
      const label = refLabel?.(value);
      if (!label) {
        incomplete = true;
        return '';
      }
      return label;
    }
    if (typeof value === 'object') {
      incomplete = true;
      return '';
    }
    return summaryEnumLabel(mod, key, value);
  });

  if (incomplete) return null;
  const collapsed = text.replace(/\s+/g, ' ').trim();
  return collapsed || null;
}

/**
 * Anzeigewert eines Skalars: bei einem `enum` mit `x-enumLabels` dessen Label.
 *
 * Ohne das stünde auf der Karte der technische Wert (`erfuellt`), den im Panel niemand zu sehen
 * bekommt — dort rendert das Status-Widget längst das Label.
 */
function summaryEnumLabel(mod: ModuleManifest | null | undefined, key: string, value: any): string {
  const prop = (mod?.settingsSchema as any)?.properties?.[key]
    ?? (mod?.inputSchema as any)?.properties?.[key];
  const labels = prop?.['x-enumLabels'];
  const label = labels?.[String(value)];
  return label != null ? String(label) : String(value);
}

/** Turn a parameter name into a unique, stable property key (for ${node.outputs.<key>} refs). */
export function slugKey(name: string, used: Set<string>): string {
  let base = (name ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (!base || !/^[a-z_]/.test(base)) base = base ? `p_${base}` : 'param';
  let key = base;
  let i = 1;
  while (used.has(key)) {
    i += 1;
    key = `${base}_${i}`;
  }
  used.add(key);
  return key;
}

/** Map a configured Eingangsparameter type to a JSON-schema type for the output port. */
function paramJsonType(typ?: string): string {
  const t = (typ ?? '').trim().toLowerCase();
  if (t === 'zahl' || t === 'währung' || t === 'waehrung' || t === 'number') return 'number';
  if (t === 'liste' || t === 'list' || t === 'array') return 'array';
  if (t === 'boolean' || t === 'bool') return 'boolean';
  return 'string';
}

/**
 * Dateitypen, die als Text indexiert sind und deshalb extrahiert werden können.
 * Spiegelt `DOKUMENT_DATEITYPEN` im Python-`eingangsparameter`-Handler.
 */
const DOKUMENT_DATEITYPEN = new Set(['PDF', 'WORD', 'EXCEL', 'POWERPOINT']);

/** Nicht-Text-Dateitypen: Binärgeometrie, dort gibt es nichts zu extrahieren. */
const GEOMETRIE_DATEITYPEN = new Set(['IFC', 'COORDINATION']);

/**
 * True, wenn die Regel auf einem Dateityp arbeitet, der nicht als Text indexiert ist (z.B. IFC).
 *
 * Dann läuft der Eingangsparameter-Knoten im **Datei-Modus**: er extrahiert nichts, sondern reicht
 * die Dateireferenz weiter. Muss mit `_ist_datei_modus` im Python-Handler übereinstimmen.
 *
 * Bewusst konservativ — nur eine Auswahl, die **ausschließlich** aus Geometrietypen besteht,
 * schaltet um:
 * - leer heißt "alle Dateitypen" und bleibt Dokument-Modus,
 * - eine gemischte Auswahl (IFC *und* PDF) bleibt Dokument-Modus, dort ist die Extraktion gewollt,
 * - unbekannte Tokens (auch `UNKNOWN`) schalten nicht um, weil über deren Inhalt nichts bekannt ist.
 *
 * Normalisiert wird über `normalizeFileTypeToken`, weil Regelsets die Typen teils als Endung
 * (`.ifc`, `.xlsx`) und teils als Namen (`IFC`, `EXCEL`) speichern.
 */
export function isDateiModus(config?: { [key: string]: any }): boolean {
  const raw = config?.['erlaubte_dateitypen'];
  if (!Array.isArray(raw) || !raw.length) return false;
  const typen = raw.map(t => normalizeFileTypeToken(t)).filter(Boolean);
  if (!typen.length) return false;
  if (typen.some(t => DOKUMENT_DATEITYPEN.has(t))) return false;
  return typen.every(t => GEOMETRIE_DATEITYPEN.has(t));
}

/**
 * Ausgänge des Eingangsparameter-Knotens im Datei-Modus: die Dateireferenz statt extrahierter
 * Parameter. Nachgelagerte IFC-Module binden an `${trigger_0.outputs.project_files}` bzw.
 * `.file_uri` — ohne diese Ports gibt es im Canvas nichts zum Verbinden.
 */
function buildDateiOutputSchema(): { [key: string]: any } {
  return {
    type: 'object',
    properties: {
      project_files: {title: 'Projektdateien', type: 'array'},
      file_uri: {title: 'Datei-URI', type: 'string'},
      file_name: {title: 'Dateiname', type: 'string'},
    },
  };
}

/**
 * Build the trigger `config.outputSchema` for the "eingangsparameter" node from its
 * user-configured extraction parameters. Each configured parameter becomes one output
 * port (key = slug(name), title = name), so downstream steps can reference
 * `${trigger_0.outputs.<key>}`. Contains ONLY the configured parameters — no other outputs.
 * In einer Regel "versteckte" Parameter (`hidden: true`) werden übersprungen (nicht extrahiert,
 * kein Output-Port) — spiegelt das Verhalten des Python-`eingangsparameter`-Handlers.
 *
 * Im Datei-Modus (z.B. IFC) treten stattdessen die Datei-Ports an ihre Stelle: dort wird nichts
 * extrahiert, die konfigurierten Parameter haben also keine Entsprechung im Ergebnis.
 */
export function buildParameterOutputSchema(config?: { [key: string]: any }): { [key: string]: any } {
  if (isDateiModus(config)) return buildDateiOutputSchema();
  const params = Array.isArray(config?.['parameter']) ? config!['parameter'] : [];
  const used = new Set<string>();
  const properties: { [key: string]: any } = {};
  for (const p of params) {
    if (p?.hidden === true) continue;
    const name = (p?.name ?? '').trim();
    if (!name) continue;
    const key = slugKey(name, used);
    const property: { [k: string]: any } = {title: name, type: paramJsonType(p?.typ ?? p?.type)};
    // Auswahl-Parameter geben ihre zulässigen Werte als `enum` weiter. Damit zeigt der Wert-Picker
    // sie an (resolveVariableChip liest `enum`) und ein Auswahl-Ausgang bleibt als solcher
    // erkennbar, ohne dass eine zweite Stelle die Optionen kennen muss.
    const options = Array.isArray(p?.optionen) ? p.optionen : (Array.isArray(p?.options) ? p.options : []);
    const cleaned = options.map((option: any) => String(option ?? '').trim()).filter(Boolean);
    if (cleaned.length) property['enum'] = cleaned;
    properties[key] = property;
  }
  return {type: 'object', properties};
}

/** Auf Regelset-Ebene definierter Eingabeparameter (Projektion für den eingangsparameter-Knoten). */
export interface SetParamDef {
  id: string;
  name: string;
  beschreibung?: string;
  dataType?: string;
  /** Zulässige Werte bei `dataType: 'Auswahl'` – wandern als `optionen` in den Knoten. */
  options?: string[];
}

/**
 * Gleicht die `parameter`-Liste eines eingangsparameter-Knotens gegen die Regelset-Parameter ab:
 * - Reihenfolge/Definition (Name/Typ/Beschreibung) folgen dem Set (Single Source of Truth).
 * - Der per-Regel `hidden`-Zustand bekannter Parameter bleibt erhalten.
 * - **Erst-Abgleich** (Migration bestehender Regeln / neue Regel — der Knoten trägt noch keine
 *   Set-IDs): zunächst werden ALLE Parameter angezeigt (`hidden: false`).
 * - **Nachträglich** zum Set ergänzter Parameter (Knoten war bereits abgeglichen): startet
 *   versteckt (`hidden: true`).
 * - Der Identifikator ist immer sichtbar; Parameter, die es im Set nicht (mehr) gibt, entfallen.
 */
export function reconcileEingangParameters(
  existing: any[] | undefined,
  setParams: SetParamDef[],
  identifikatorParamId: string,
): Array<Record<string, unknown>> {
  const byId = new Map<string, any>();
  for (const p of existing ?? []) {
    if (p && typeof p.id === 'string' && p.id) byId.set(p.id, p);
  }
  const named = (setParams ?? []).filter(sp => (sp?.name ?? '').trim().length > 0);
  // "Bereits abgeglichen" = mind. ein Set-Parameter ist schon im Knoten vorhanden. Nur dann gilt
  // ein neu ergänzter Set-Parameter als nachträglich (→ versteckt); sonst ist es der Erst-Abgleich.
  const alreadySynced = named.some(sp => byId.has(sp.id));
  return named.map(sp => {
    const prev = byId.get(sp.id);
    const isIdentifikator = !!identifikatorParamId && sp.id === identifikatorParamId;
    let hidden: boolean;
    if (isIdentifikator) {
      hidden = false;
    } else if (prev) {
      hidden = prev.hidden === true;   // bekannter Parameter: Zustand erhalten
    } else if (!alreadySynced) {
      hidden = false;                  // Erst-Abgleich: alles sichtbar
    } else {
      hidden = true;                   // nachträglich ergänzter Set-Parameter: versteckt
    }
    return {
      id: sp.id,
      name: sp.name,
      beschreibung: sp.beschreibung ?? '',
      typ: sp.dataType ?? 'Text',
      // Die Optionen MÜSSEN mit: diese Funktion baut das Knotenobjekt neu, jedes hier nicht
      // aufgeführte Feld ist danach weg. Ohne sie käme ein Auswahl-Parameter ohne zulässige Werte
      // am `eingangsparameter`-Knoten an und die Extraktion liefe unbeschränkt.
      optionen: sp.options ?? [],
      hidden,
    };
  });
}

/** Extract UI position from StepNode.inputs or TriggerNode.config */
export function getNodePosition(data?: { [key: string]: any }): { x: number; y: number } {
  const ui = data?.['__ui'];
  if (ui?.position) {
    return {x: ui.position.x ?? 0, y: ui.position.y ?? 0};
  }
  return {x: 0, y: 0};
}

/** Inject UI position into inputs/config dict */
export function setNodePosition(data: { [key: string]: any }, x: number, y: number): void {
  data['__ui'] = {position: {x, y}};
}

/** Get user-given name from inputs/config, falling back to module name */
export function getNodeName(data?: { [key: string]: any }, module?: ModuleManifest | null): string {
  return data?.['__name'] ?? module?.name ?? 'Schritt';
}

/** Store user-given name in inputs/config */
export function setNodeName(data: { [key: string]: any }, name: string): void {
  data['__name'] = name;
}

/** Strip internal __ui / __name keys from inputs for display */
export function cleanInputs(data?: { [key: string]: any }): { [key: string]: any } {
  if (!data) return {};
  const {__ui, __name, ...rest} = data;
  return rest;
}

/** Decode onSuccess: comma-separated string → string[] */
export function decodeOnSuccess(value?: string | null): string[] {
  if (!value) return [];
  if (value.includes(',')) {
    return value.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [value];
}

/** Encode onSuccess: string[] → comma-separated string */
export function encodeOnSuccess(values: string[]): string | undefined {
  if (!values || values.length === 0) return undefined;
  return values.join(',');
}

/** Trigger-type labels for display */
const TRIGGER_LABELS: Record<string, string> = {
  ON_FILE_UPLOAD: 'Bei Dateiupload',
  ON_CHECK_EXECUTION: 'Bei Ausführung der Prüfung',
  ON_SCHEDULE: 'Geplant',
  ON_WEBHOOK: 'Webhook',
};

export function getTriggerLabel(triggerModuleId?: string): string {
  if (!triggerModuleId) return '';
  return TRIGGER_LABELS[triggerModuleId] ?? triggerModuleId;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Edge sync: write canvas edges back into manifest onSuccess/onError
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Write FlowEdge[] back into the Manifest's onSuccess/onError/onThen/onElse/onBranch fields
 * (inverse of buildEdgesFromManifest).
 *
 * Which field a flow edge lands in is decided per source node by {@link resolveBranchSpec}:
 * no spec → `onSuccess`; legacy binary → `onThen`/`onElse` (port 0/1); n-wertig →
 * `onBranch[cases[portIndex].key]`. Error-type edges always go to `onError`.
 */
export function syncEdgesToManifest(manifest: Manifest, edges: FlowEdge[], modules?: ModuleManifest[]): void {
  // Clear all existing connections
  if (manifest.trigger) {
    manifest.trigger.onSuccess = undefined;
  }
  for (const step of manifest.steps ?? []) {
    step.onSuccess = undefined;
    step.onError = undefined;
    step.onThen = undefined;
    step.onElse = undefined;
    step.onBranch = undefined;
    (step as any).branchOn = undefined;
  }

  // Branch spec per nodeId. Built from module manifest AND the node's settings, because
  // branching can be conditional on a setting (Prüfmodus) — see resolveBranchSpec.
  const moduleById = new Map<string, ModuleManifest>();
  for (const m of modules ?? []) moduleById.set(m.moduleId, m);
  const specBySource = new Map<string, BranchSpec | null>();
  const flowPortCountBySource = new Map<string, number>();
  for (const step of manifest.steps ?? []) {
    const nodeId = step.nodeId ?? '';
    const mod = moduleById.get(step.moduleId ?? '');
    const spec = resolveBranchSpec(mod, step.settings);
    specBySource.set(nodeId, spec);
    flowPortCountBySource.set(nodeId, spec ? spec.cases.length : 1);
  }

  // Flow edges define execution order: source port index < number of flow-out ports.
  // Everything at or beyond that index is a data edge and is written as an ${...} input ref.
  const flowEdges = edges.filter(e =>
    e.targetPortIndex === 0 &&
    e.sourcePortIndex < (flowPortCountBySource.get(e.source) ?? 1));

  const successBySource = new Map<string, string[]>();
  const errorBySource = new Map<string, string>();
  /** nodeId → branch key → targets */
  const branchBySource = new Map<string, Map<string, string[]>>();

  const addTo = (map: Map<string, string[]>, key: string, value: string) => {
    const list = map.get(key) ?? [];
    list.push(value);
    map.set(key, list);
  };

  for (const edge of flowEdges) {
    if (edge.type === 'error') {
      errorBySource.set(edge.source, edge.target);
      continue;
    }
    const spec = specBySource.get(edge.source);
    const branchCase = spec?.cases[edge.sourcePortIndex];
    if (!spec || !branchCase) {
      addTo(successBySource, edge.source, edge.target);
      continue;
    }
    const byKey = branchBySource.get(edge.source) ?? new Map<string, string[]>();
    addTo(byKey, branchCase.key, edge.target);
    branchBySource.set(edge.source, byKey);
  }

  // Write back to trigger
  const triggerId = manifest.trigger?.nodeId ?? 'trigger_0';
  if (manifest.trigger) {
    manifest.trigger.onSuccess = encodeOnSuccess(successBySource.get(triggerId) ?? []);
  }

  // Write back to steps
  for (const step of manifest.steps ?? []) {
    const nodeId = step.nodeId ?? '';
    step.onSuccess = encodeOnSuccess(successBySource.get(nodeId) ?? []);
    step.onError = errorBySource.get(nodeId) ?? undefined;

    const byKey = branchBySource.get(nodeId);
    const spec = specBySource.get(nodeId);
    if (!byKey || !spec) continue;

    if (spec.legacy === 'thenElse') {
      step.onThen = encodeOnSuccess(byKey.get('dann') ?? []);
      step.onElse = encodeOnSuccess(byKey.get('sonst') ?? []);
    } else {
      const onBranch: { [key: string]: string } = {};
      for (const branchCase of spec.cases) {
        const encoded = encodeOnSuccess(byKey.get(branchCase.key) ?? []);
        if (encoded) onBranch[branchCase.key] = encoded;
      }
      step.onBranch = Object.keys(onBranch).length ? onBranch : undefined;
      // Quell-Schlüssel explizit mitschreiben: bei frei benannten Nutzer-Optionen (z.B.
      // Ausgabeparameter „Arbeitsraum" mit Ja/Nein) kann das Backend die Entscheidung sonst nur
      // über Werte-Raten finden — ein zweiter Parameter mit demselben Wert würde sie kippen.
      // Siehe BranchDecision.resolveKey.
      (step as any).branchOn = step.onBranch ? spec.outputKey : undefined;
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Edge derivation from Manifest
// ═══════════════════════════════════════════════════════════════════════════════

/** Regex to extract nodeId and field from a variable reference like ${nodeId.outputs.field} */
const VAR_REF_REGEX = /^\$\{([^.]+)\.outputs\.([^}]+)\}$/;

/**
 * Baut die Flow-Kanten aus den `onSuccess`/`onError`/`onThen`/`onElse`/`onBranch`-Feldern.
 *
 * Datenkanten entstehen hier nicht mehr: Wertbindungen leben in `step.inputs` und werden im
 * Konfig-Panel gepflegt. Die Leinwand zeigt ausschließlich den Ablauf.
 */
export function buildEdgesFromManifest(manifest: Manifest, modules?: ModuleManifest[]): FlowEdge[] {
  const edges: FlowEdge[] = [];
  const edgeSet = new Set<string>(); // dedup key: "source:srcPort→target:tgtPort"
  const moduleMap = new Map<string, ModuleManifest>();
  if (modules) {
    for (const m of modules) moduleMap.set(m.moduleId, m);
  }

  // Build a map of all nodes for port count lookup
  const addEdgeWithPorts = (sourceId: string, sourcePortIndex: number, targetId: string, targetPortIndex: number, type: 'success' | 'error') => {
    const dedupKey = `${sourceId}:${sourcePortIndex}→${targetId}:${targetPortIndex}`;
    if (edgeSet.has(dedupKey)) return;
    edgeSet.add(dedupKey);
    edges.push({
      id: `edge_${sourceId}_${targetId}_${type}_${sourcePortIndex}_${targetPortIndex}`,
      source: sourceId,
      sourcePortIndex,
      target: targetId,
      targetPortIndex,
      label: '',
      type,
    });
  };

  // ── 1. Build FLOW edges from onSuccess/onError/onThen/onElse chains ──
  const addFlowEdge = (sourceId: string, targetId: string, type: 'success' | 'error', sourcePortIndex = 0) => {
    // Flow-In port is at index 0 on target; branching sources use port 0 (Dann) / 1 (Sonst)
    addEdgeWithPorts(sourceId, sourcePortIndex, targetId, 0, type);
  };

  // Trigger → next node(s)
  if (manifest.trigger) {
    const targets = decodeOnSuccess(manifest.trigger.onSuccess);
    for (const target of targets) {
      addFlowEdge(manifest.trigger.nodeId ?? 'trigger_0', target, 'success');
    }
  }

  // Step flow edges
  for (const step of manifest.steps ?? []) {
    const targets = decodeOnSuccess(step.onSuccess);
    for (const target of targets) {
      addFlowEdge(step.nodeId ?? '', target, 'success');
    }
    if (step.onError) {
      addFlowEdge(step.nodeId ?? '', step.onError, 'error');
    }
    // Branch edges of branching nodes: Dann = flow port 0, Sonst = flow port 1.
    // Ein Bestands-`onSuccess` eines jetzt verzweigenden Moduls landet oben ebenfalls auf
    // Port 0 (= Dann) und wird beim nächsten Speichern als `onThen` neu geschrieben.
    for (const target of decodeOnSuccess(step.onThen)) {
      addFlowEdge(step.nodeId ?? '', target, 'success', 0);
    }
    for (const target of decodeOnSuccess(step.onElse)) {
      addFlowEdge(step.nodeId ?? '', target, 'success', 1);
    }
    // n-wertige Zweige: Port-Index = Position des Keys in den deklarierten Cases.
    // Selbstheilend wie oben: ist der Zweig nicht (mehr) deklariert — z.B. weil der Prüfmodus
    // abgeschaltet wurde — landet die Kante auf Port 0 und wird beim nächsten Speichern als
    // `onSuccess` geschrieben, statt lautlos zu verschwinden.
    if (step.onBranch) {
      const spec = resolveBranchSpec(moduleMap.get(step.moduleId ?? ''), step.settings);
      for (const [caseKey, refs] of Object.entries(step.onBranch)) {
        const portIndex = Math.max(0, spec?.cases.findIndex(c => c.key === caseKey) ?? -1);
        for (const target of decodeOnSuccess(refs)) {
          addFlowEdge(step.nodeId ?? '', target, 'success', portIndex);
        }
      }
    }
  }

  // Aus `step.inputs` entstehen KEINE Kanten mehr. Wertbindungen sind dort weiterhin als
  // `${nodeId.outputs.key}` gespeichert, werden aber im Konfig-Panel gepflegt und angezeigt
  // (siehe resolveVariableReference) — die Leinwand zeigt nur noch den Ablauf.

  return edges;
}

/** A single rule step described technically for AI interpretation (module + resolved operation). */
export interface RuleStepDescription {
  modul: string;
  details: string;
}

/**
 * Return a rule's steps in execution/flow order (following the trigger's and each step's
 * onThen/onElse/onSuccess chains) instead of the raw `manifest.steps` array order. Steps not
 * reachable from the trigger are appended in their original order. Deterministic — so callers
 * that align data by index (interpretation ↔ step) can rely on the same ordering.
 */
export function orderStepsByFlow(manifest: Manifest): StepNode[] {
  const steps = manifest.steps ?? [];
  const byId = new Map<string, StepNode>();
  for (const s of steps) if (s.nodeId) byId.set(s.nodeId, s);

  const nextIds = (node?: {
    onSuccess?: string;
    onThen?: string;
    onElse?: string;
    onBranch?: { [key: string]: string };
  }): string[] => node ? [
    ...decodeOnSuccess(node.onThen),
    ...decodeOnSuccess(node.onElse),
    // Ohne die onBranch-Ziele fielen die Nachfolger eines Prüfmodus-Knotens aus der
    // Regelübersicht, der KI-Interpretation und der Schritt-Reihenfolge des Hybrid-Editors.
    ...Object.values(node.onBranch ?? {}).flatMap(refs => decodeOnSuccess(refs)),
    ...decodeOnSuccess(node.onSuccess),
  ] : [];

  const ordered: StepNode[] = [];
  const seen = new Set<string>();
  const queue: string[] = [...nextIds(manifest.trigger)];
  while (queue.length) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const step = byId.get(id);
    if (step) {
      ordered.push(step);
      queue.push(...nextIds(step));
    }
  }
  // Append steps not reachable from the trigger, keeping their original order.
  for (const s of steps) {
    if (!s.nodeId || !seen.has(s.nodeId)) {
      ordered.push(s);
    }
  }
  return ordered;
}

/**
 * Serialize a rule's steps into compact, technical descriptions for the AI-interpretation endpoint:
 * per step the module name plus its meaningful settings (operator, formula, …) and inputs, with
 * `${node.outputs.field}` references resolved to human-readable source names. The AI turns these
 * facts into natural-language German sentences for the rule overview.
 */
export function describeRuleSteps(manifest: Manifest, modules: ModuleManifest[] = []): RuleStepDescription[] {
  const moduleMap = new Map<string, ModuleManifest>();
  for (const m of modules) moduleMap.set(m.moduleId ?? '', m);

  const nameById = new Map<string, string>();
  const outSchemaById = new Map<string, any>();

  const trigger = manifest.trigger;
  if (trigger?.nodeId) {
    const tmod = moduleMap.get(trigger.moduleId ?? '');
    nameById.set(trigger.nodeId, trigger.config?.['__name'] ?? tmod?.name ?? 'Eingang');
    outSchemaById.set(trigger.nodeId, trigger.config?.['outputSchema'] ?? tmod?.outputSchema);
  }
  for (const step of manifest.steps ?? []) {
    if (!step.nodeId) continue;
    const smod = moduleMap.get(step.moduleId ?? '');
    nameById.set(step.nodeId, step.inputs?.['__name'] ?? smod?.name ?? step.moduleId ?? 'Schritt');
    outSchemaById.set(step.nodeId, buildDataOutputSchema(step.moduleId, step.settings, smod?.outputSchema));
  }

  const resolveRef = (ref: string): string | null => {
    const m = VAR_REF_REGEX.exec(ref);
    if (!m) return null;
    const [, srcId, field] = m;
    const srcName = nameById.get(srcId) ?? srcId;
    const title = outSchemaById.get(srcId)?.properties?.[field]?.title
      ?? field.replace(/[_-]+/g, ' ').trim();
    return `${title} (aus "${srcName}")`;
  };

  const describeValue = (v: any): string => {
    if (typeof v === 'string') return resolveRef(v) ?? `"${v}"`;
    if (Array.isArray(v)) return v.map(describeValue).join(', ');
    if (v && typeof v === 'object') {
      return Object.entries(v).map(([k2, v2]) => `${k2}: ${describeValue(v2)}`).join(', ');
    }
    return String(v);
  };

  return orderStepsByFlow(manifest).map(step => {
    const smod = moduleMap.get(step.moduleId ?? '');
    const modul = smod?.name ?? step.inputs?.['__name'] ?? step.moduleId ?? 'Schritt';
    const parts: string[] = [];

    const setProps = smod?.settingsSchema?.properties ?? {};
    for (const [k, v] of Object.entries(step.settings ?? {})) {
      if (v == null || v === '' || k.startsWith('__')) continue;
      const label = (setProps as any)[k]?.title ?? k;
      parts.push(`${label}: ${typeof v === 'object' ? JSON.stringify(v) : v}`);
    }

    const inProps = smod?.inputSchema?.properties ?? {};
    for (const [k, v] of Object.entries(cleanInputs(step.inputs))) {
      if (k.startsWith('__')) continue; // interne Keys (z.B. __aiInterpretation) nicht an die KI geben
      if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
      const label = (inProps as any)[k]?.title ?? k;
      parts.push(`${label} = ${describeValue(v)}`);
    }

    return {modul, details: parts.join('; ')};
  });
}

/** Semantic colour of a result badge (Erfüllt → success, Teilweise → warning, Nicht erfüllt → error). */
export type RuleResultTone = 'success' | 'warning' | 'error' | 'neutral';

/** A possible result of a rule (label = outcome/output name, description = its meaning). */
export interface RuleResultItem {
  label: string;
  description: string;
  tone: RuleResultTone;
}

const NUTZERANTWORT_STATUS_LABELS: Record<string, string> = {
  erfuellt: 'Erfüllt',
  teilweise_erfuellt: 'Teilweise erfüllt',
  nicht_erfuellt: 'Nicht erfüllt',
  // Spiegel von STATUS_UNBEKANNT im Modul (nutzerantwort/handler.py): ein Zweig, der bewusst
  // kein Prüfergebnis liefert, statt eines unbewerteten Ergebnisses im Bericht.
  unbekannt: 'Unbekannt (nicht relevant)',
};

/**
 * Ausgabeparameter, dessen Wert das Prüfergebnis trägt. Spiegelt `VERDICT_KEY_RE` in
 * `BIC-Workflows/shared/ai_verdict.py` und `VERDICT_COLUMN_RE` in `report-display-config.ts` —
 * alle drei Listen müssen identisch bleiben.
 */
const VERDICT_PARAM_RE = /^(ergebnis|result|erf[üu]llt|erfuellt|bestanden|passed|erfolgreich)$/i;

/** Ergebnisse eines KI-Anweisung-Knotens im Prüfmodus (Reihenfolge = Reihenfolge der Flow-Ports). */
const PRUEFMODUS_RESULTS: RuleResultItem[] = [
  {label: 'Erfüllt', description: 'Die Anweisung wurde erfüllt.', tone: 'success'},
  {label: 'Hinweis', description: 'Die Anweisung wurde teilweise erfüllt.', tone: 'warning'},
  {label: 'Nicht erfüllt', description: 'Die Anweisung wurde nicht erfüllt.', tone: 'error'},
  {
    label: 'Unbekannt',
    description: 'Nicht entscheidbar oder für diese Regel nicht relevant.',
    tone: 'neutral',
  },
];

/** Map a Nutzerantwort status value to a badge tone (order matters: 'nicht' before 'erf'). */
function statusTone(status: string): RuleResultTone {
  const s = status.toLowerCase();
  if (s.includes('nicht')) return 'error';
  if (s.includes('teil')) return 'warning';
  if (s.includes('erf')) return 'success';
  return 'neutral';
}

/**
 * Derive a rule's possible results (raw, no AI) from its terminal-ish steps:
 * each Nutzerantwort → one outcome (status label + answer text); each KI-Anweisung → its
 * configured output parameters. Used as the AI input and as the pre-interpretation fallback.
 */
export function deriveRuleResults(manifest: Manifest): RuleResultItem[] {
  const items: RuleResultItem[] = [];
  for (const step of orderStepsByFlow(manifest)) {
    const mid = (step.moduleId ?? '').toLowerCase();
    if (mid === NUTZERANTWORT_MODULE_ID) {
      const status = (step.settings?.['status'] ?? '').toString();
      items.push({
        label: NUTZERANTWORT_STATUS_LABELS[status] ?? (status || 'Ergebnis'),
        description: (step.settings?.['antwort'] ?? '').toString(),
        tone: statusTone(status),
      });
    } else if (mid === AI_INSTRUCTION_MODULE_ID) {
      // Im Prüfmodus kommen die drei Zweige HINZU — der Modus ist nur eine Weiterleitung, die
      // konfigurierten Ausgabeparameter bleiben eigenständige Ergebnisse. Ausgenommen ist der
      // Verdikt-Parameter selbst, den die drei Zweige bereits abbilden.
      const pruefmodus = !!step.settings?.['pruefmodus'];
      if (pruefmodus) {
        items.push(...PRUEFMODUS_RESULTS);
      }
      const params = Array.isArray(step.settings?.['ausgabeparameter']) ? step.settings!['ausgabeparameter'] : [];
      for (const p of params) {
        const name = (p?.name ?? '').toString().trim();
        if (!name) continue;
        if (pruefmodus && VERDICT_PARAM_RE.test(name)) continue;
        items.push({label: name, description: (p?.beschreibung ?? '').toString(), tone: 'neutral'});
      }
    }
  }
  return items;
}

/** Footer text describing the report granularity, derived from the rule's Prüfebene. */
export function ruleResultScope(manifest: Manifest): string {
  switch ((manifest.trigger?.config?.['pruefebene'] ?? '').toString()) {
    case 'lv_position':
      return 'Bericht mit Ergebnis pro LV-Position';
    case 'kapitel':
      return 'Bericht mit Ergebnis pro Kapitel';
    case 'element':
      return 'Bericht mit Ergebnis pro Element';
    case 'gesamtdokument':
      return 'Bericht mit Ergebnis für das Gesamtdokument';
    default:
      return '';
  }
}

/**
 * Hybrid-spezifische Modul-Kategorien für die Sidebar (statischer Fallback, wenn
 * {@link buildCategoriesFromModules} nichts liefert).
 *
 * Nicht enthalten, weil die Module `INTERN` sind und deshalb nicht zum Hinzufügen angeboten
 * werden: **Eingangsparameter** (Einstiegs-Trigger, existiert genau einmal je Regel und wird
 * automatisch erzeugt) sowie **Wertevergleich** und **Textvergleich** (abgelöst durch
 * „Bedingung", die mehrere Bedingungen mit Datentypen und beliebig viele benannte Ausgänge kann).
 */
export const HYBRID_MODULE_CATEGORIES: ModuleCategoryInfo[] = [
  {
    category: 'LOGIC',
    label: 'Bedingung',
    description: 'Verzweigt den Ablauf über mehrere Bedingungen und benannte Fälle',
    color: '#6366F1',
    bgColor: '#E0E7FF',
    abbrev: 'L',
    icon: faCirclesOverlap
  },
  {
    category: 'VARIABLE',
    label: 'Variablen',
    description: 'Variable mit verschiedenen Datentypen',
    color: '#8B5CF6',
    bgColor: '#EDE9FE',
    abbrev: 'V',
    icon: faInputNumeric
  },
  {
    category: 'AI',
    label: 'KI-Anweisung',
    description: 'Sendet einen Prompt an ein KI-Modell',
    color: '#A855F7',
    bgColor: '#F3E8FF',
    abbrev: 'AI',
    icon: faSparkles
  },
  {
    category: 'CONTEXT',
    label: 'KI-Kontext',
    description: 'Gibt Hintergrundinformationen für KI-Modelle',
    color: '#F59E0B',
    bgColor: '#FEF3C7',
    abbrev: 'Cx',
    icon: faBookOpen
  },
  {
    category: 'FILES',
    label: 'Berichte Generator',
    description: 'Erstellt strukturierte Berichte in definiertem Format',
    color: '#22C55E',
    bgColor: '#DCFCE7',
    abbrev: 'F',
    icon: faFileLines
  },
];

/** Geteilte Module für Hybrid-Prüfregeln */
export const HYBRID_SHARED_CATEGORIES: ModuleCategoryInfo[] = [
  {
    category: 'AI',
    label: 'KI-Systemprompt',
    description: 'Systemprompt für die KI Prüfung',
    color: '#A855F7',
    bgColor: '#F3E8FF',
    abbrev: 'AI',
    icon: faSparkles
  },
];

export interface FlowModule {
  moduleId: string;
  version: string;
  name: string;
  category: ModuleCategory;
  description: string;
  icon: string;
  inputSchema: Record<string, any>;
  outputSchema: Record<string, any>;
}
